import React, { useState, useRef, useEffect, useMemo } from 'react';
import { CheckCircle, MapPin, Navigation, X, Search, ChevronDown, FileText, AlertCircle } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import Stepper from 'react-stepper-horizontal';
import { onboardClinicVendor, updateClinicVendor } from '../../../services/SalesExecutive/vendorOnboardingService';
import { useAuth } from '../../../hooks/useAuth';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { stateCityList } from '../../../data/stateCityList';
import {
  medicalCouncils,
  languagesSpoken,
  getEducationalQualifications,
  getSpecializations,
} from '../../../constants/professional_details_options';
import { verifyIfscCode } from '../../../services/MasterAdmin/vendorBankDetailsService';
import api from '../../../utils/api';

// ─── Catalogs ────────────────────────────────────────────────────────────────
const ALL_QUALIFICATIONS: string[] = getEducationalQualifications() as string[];
const ALL_SPECIALIZATIONS: string[] = getSpecializations() as string[];
const ALL_LANGUAGES: string[] = languagesSpoken;
const ALL_COUNCILS: string[] = medicalCouncils;

const IDENTITY_PROOF_TYPES = ['Aadhar Card', 'Driving License', 'PAN Card'];
const identityRequiresBack = (type: string) => String(type || '').trim() === 'Aadhar Card';

const CONSULTATION_TYPES = ['Online', 'Offline (In-Clinic)', 'Instant'];
const DAYS_LIST = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const FLOOR_OPTIONS = ['Ground Floor', ...Array.from({ length: 12 }, (_, i) => String(i + 1)), 'Other'];
const MIN_REGISTRATION_DATE = '1950-01-01';
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_DOC_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

// ─── Regexes (same as Vedika Web) ─────────────────────────────────────────────
const nameRegex = /^[a-zA-Z\s.-]+$/;
const clinicRegex = /^[a-zA-Z0-9\s.,\-&']+$/;
const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/;
const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const isValidIfsc = (code: string) => IFSC_PATTERN.test(String(code || '').trim().toUpperCase());

const isUuid = (val: string) => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(val);

const formConsultationTypesForApi = (formTypes: string[]) =>
  (formTypes || []).map((t) => (t === 'Offline (In-Clinic)' ? 'Offline' : t));

const requiresSpecialityWiseFees = (formTypes: string[]) => {
  const api = formConsultationTypesForApi(formTypes);
  return api.includes('Online') || api.includes('Offline');
};

const feeLabelFromStoredSpeciality = (stored: string) => {
  const trimmed = String(stored || '').trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  if (lower === 'other') return '';
  const m = trimmed.match(/^other\s*:\s*(.+)$/i);
  if (m) return (m[1] || '').trim();
  return trimmed;
};

const specsEligibleForFees = (specs: string[]) => {
  const standard: string[] = [];
  const customOther: string[] = [];
  const seen = new Set<string>();
  for (const s of specs || []) {
    if (s == null) continue;
    const trimmed = String(s).trim();
    if (!trimmed) continue;
    const lower = trimmed.toLowerCase();
    if (lower === 'null' || lower === 'undefined') continue;
    if (isUuid(trimmed)) continue;
    const m = trimmed.match(/^other\s*:\s*(.+)$/i);
    if (m) {
      const label = (m[1] || '').trim();
      if (!label) continue;
      const key = label.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        customOther.push(label);
      }
    } else if (lower === 'other') {
      continue;
    } else {
      const key = lower;
      if (!seen.has(key)) {
        seen.add(key);
        standard.push(trimmed);
      }
    }
  }
  return [...standard, ...customOther];
};

const syncSpecialityFeeRows = (specs: string[], prevList: any[]) => {
  const labels = specsEligibleForFees(specs);
  const prevByKey: Record<string, any> = {};
  (prevList || []).forEach((r) => {
    const label = feeLabelFromStoredSpeciality(r.speciality);
    if (!label) return;
    prevByKey[label.toLowerCase()] = r;
  });
  const emptyPair = () => ({ firstVisitFees: '', followUpFees: '' });
  return labels.map((label) => {
    const k = String(label).trim().toLowerCase();
    const ex = prevByKey[k];
    if (ex && ex.feesByConsultationType && typeof ex.feesByConsultationType === 'object') {
      const btc = ex.feesByConsultationType;
      const on = btc.online || {};
      const off = btc.offline || {};
      return {
        speciality: label,
        feesByConsultationType: {
          online: {
            firstVisitFees: String(on.firstVisitFees ?? '').trim(),
            followUpFees: String(on.followUpFees ?? on.followupFees ?? '').trim(),
          },
          offline: {
            firstVisitFees: String(off.firstVisitFees ?? '').trim(),
            followUpFees: String(off.followUpFees ?? off.followupFees ?? '').trim(),
          },
        },
      };
    }
    const legacyFirst = String(ex?.firstVisitFees ?? '').trim();
    const legacyFollow = String(ex?.followUpFees ?? ex?.followupFees ?? '').trim();
    if (ex && (legacyFirst || legacyFollow)) {
      return {
        speciality: label,
        feesByConsultationType: {
          online: { firstVisitFees: legacyFirst, followUpFees: legacyFollow },
          offline: { firstVisitFees: legacyFirst, followUpFees: legacyFollow },
        },
      };
    }
    return {
      speciality: label,
      feesByConsultationType: {
        online: emptyPair(),
        offline: emptyPair(),
      },
    };
  });
};

const feeChannelNeeds = (formTypes: string[]) => {
  const api = formConsultationTypesForApi(formTypes);
  return {
    needOnline: api.includes('Online'),
    needOffline: api.includes('Offline'),
  };
};

const feeRowComplete = (row: any, needOnline: boolean, needOffline: boolean) => {
  const btc = row?.feesByConsultationType || {};
  const on = btc.online || {};
  const off = btc.offline || {};
  if (needOnline) {
    if (!String(on.firstVisitFees || '').trim() || !String(on.followUpFees || on.followupFees || '').trim()) {
      return false;
    }
  }
  if (needOffline) {
    if (!String(off.firstVisitFees || '').trim() || !String(off.followUpFees || off.followupFees || '').trim()) {
      return false;
    }
  }
  return true;
};

const pickDoctorLevelFeesFromMatrix = (feeList: any[]) => {
  if (!feeList || !feeList.length) return { first: '', follow: '' };
  const r = feeList[0];
  const btc = r.feesByConsultationType;
  if (btc) {
    const off = btc.offline || {};
    const on = btc.online || {};
    const first = String(off.firstVisitFees || on.firstVisitFees || '').trim();
    const follow = String(
      off.followUpFees || off.followupFees || on.followUpFees || on.followupFees || ''
    ).trim();
    return { first, follow };
  }
  const first = String(r.firstVisitFees || '').trim();
  const follow = String(r.followUpFees || r.followupFees || '').trim();
  return { first, follow };
};

const normalizeEstablishmentRoleForApi = (role: string) => {
  if (role === 'Owner of business') return 'Owner of establishment';
  if (role === 'Rented business') return 'Rented establishment';
  return role || '';
};

const getEstablishmentProofTypes = (role: string) => {
  const common = ['Electricity Bill', 'Other'];
  switch (role) {
    case 'Owner of establishment':
    case 'Owner of business':
      return ['Clinic Registration Proof', 'Shop Act', 'Property Tax Receipt(owner)', ...common];
    case 'Rented establishment':
    case 'Rented business':
      return ['Rent Agreement (tenant)', ...common];
    case 'Consultant doctor':
      return ['Letter of Association', 'Appointment Letter', 'Clinic Registration Copy', ...common];
    case 'Practicing at home':
      return ['Property Tax(Owner)', 'Rent Agreement(Tenant)', ...common];
    default:
      return common;
  }
};

// ─── Validators ──────────────────────────────────────────────────────────────
const getDoctorNameWithoutPrefix = (name: string) =>
  String(name || '').trim().replace(/^dr\.?\s*/i, '').trim();

const validateDoctorName = (name: string): string | null => {
  const trimmed = String(name || '').trim();
  if (!trimmed) return 'Full Name is required';
  if (!nameRegex.test(trimmed)) return 'Full Name can only contain letters, spaces, hyphen, and dot';
  const core = getDoctorNameWithoutPrefix(trimmed);
  if (!core) return 'Full Name is required';
  if (core.length < 3) return 'Name must be at least 3 characters';
  return null;
};

const validateContactNumber = (raw: string): { valid: boolean; message: string } => {
  const value = String(raw || '').trim().replace(/\D/g, '');
  if (!value) return { valid: false, message: 'Clinic Contact Number is required' };
  if (/^[6-9]\d{9}$/.test(value)) return { valid: true, message: '' };
  return { valid: false, message: 'Enter a valid 10-digit mobile number' };
};

const validateFile = (file: File | null, required: boolean = true): string | null => {
  if (!file) return required ? 'This document is required' : null;
  if (file.size > MAX_FILE_SIZE_BYTES) return 'File must be less than 5 MB';
  if (!ALLOWED_DOC_TYPES.includes(file.type)) return 'Only JPG, PNG or PDF allowed';
  return null;
};

const validateImageFile = (file: File | null): string | null => {
  if (!file) return null;
  if (file.size > MAX_FILE_SIZE_BYTES) return 'Image must be less than 5 MB';
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return 'Only JPG, PNG, GIF or WEBP allowed';
  return null;
};

const ESTABLISHMENT_ROLES = [
  { value: 'Owner of establishment', label: 'Owner of business', proof: 'Accepted proofs: Clinic Registration Proof / Shop Act / Property Tax Receipt (owner)' },
  { value: 'Rented establishment', label: 'Rented business', proof: 'Accepted proofs: Rent Agreement (tenant)' },
  { value: 'Consultant doctor', label: 'Consultant doctor', proof: 'Accepted proofs: Letter of Association / Appointment Letter' },
  { value: 'Practicing at home', label: 'Practicing at home', proof: 'Accepted proofs: Property Tax (Owner) / Rent Agreement (Tenant)' },
];

// ─── Step definitions ─────────────────────────────────────────────────────────
const STEPS = ['Personal Details', 'Education', 'Documentation', 'Consultation', 'Address & Bank'];

// ─── Reusable Chip Tag ────────────────────────────────────────────────────────
function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-[#004275]/10 dark:bg-[#38bdf8]/10 text-[#004275] dark:text-[#38bdf8] border border-[#004275]/20 dark:border-[#38bdf8]/20">
      {label}
      <button type="button" onClick={onRemove} className="hover:text-red-500 transition-colors ml-0.5">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

// ─── Catalog Multi-Select Dropdown ────────────────────────────────────────────
function CatalogMultiSelect({
  label, options, values, onChange, error,
}: {
  label: string;
  options: string[];
  values: string[];
  onChange: (vals: string[]) => void;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [otherText, setOtherText] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter(o => !q || o.toLowerCase().includes(q));
  }, [options, query]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (opt: string) => {
    if (values.includes(opt)) {
      if (opt === 'Other') {
        onChange(values.filter(v => v !== 'Other' && !v.startsWith('Other:')));
      } else {
        onChange(values.filter(v => v !== opt));
      }
    } else {
      onChange([...values, opt]);
    }
  };

  const hasOther = values.includes('Other');

  const addCustomOther = () => {
    const trimmed = otherText.trim();
    if (!trimmed) return;
    const newChip = `Other: ${trimmed}`;
    if (!values.includes(newChip)) {
      onChange([...values, newChip]);
    }
    setOtherText('');
  };

  const removeChip = (val: string) => {
    if (val === 'Other' || val.startsWith('Other:')) {
      onChange(values.filter(v => v !== val));
    } else {
      onChange(values.filter(v => v !== val));
    }
  };

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border bg-gray-50 dark:bg-gray-800 text-left transition-colors ${error ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300 dark:border-gray-600'} ${open ? 'ring-2 ring-[#004275] border-[#004275]' : ''}`}
      >
        <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">
          {values.length === 0 ? `Select ${label}` : `${values.filter(v => v !== 'Other').length} selected`}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-64 flex flex-col">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search..."
                className="flex-1 bg-transparent text-sm outline-none dark:text-white"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 p-1">
            {filtered.length === 0 ? (
              <div className="text-center text-sm text-gray-400 py-4">No results</div>
            ) : filtered.map(opt => {
              const checked = values.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggle(opt)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${checked ? 'bg-[#004275]/10 text-[#004275] font-semibold' : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                >
                  <span className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${checked ? 'bg-[#004275] border-[#004275]' : 'border-gray-300 dark:border-gray-600'}`}>
                    {checked && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 10"><path d="M1 5l3 3 7-7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Chips */}
      {values.filter(v => v !== 'Other').length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {values.filter(v => v !== 'Other').map(v => <Chip key={v} label={v} onRemove={() => removeChip(v)} />)}
        </div>
      )}

      {/* Other specify */}
      {hasOther && (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={otherText}
            onChange={e => setOtherText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomOther();
              }
            }}
            placeholder={`Enter Other ${label.charAt(0).toUpperCase() + label.slice(1)} *`}
            className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-black dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#004275] focus:border-[#004275]"
          />
          <button
            type="button"
            onClick={addCustomOther}
            className="p-2 bg-[#004275] hover:bg-[#003055] text-white rounded-lg flex items-center justify-center transition-colors"
            title="Add custom option"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => {
              setOtherText('');
              removeChip('Other');
            }}
            className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center transition-colors"
            title="Close input"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="h-5 mt-1">
        {error && <div className="text-xs text-red-500">{error}</div>}
      </div>
    </div>
  );
}

// ─── Document Row (matches Vedika Web's DocumentationRow) ─────────────────────
function DocumentationRow({
  label, value, onPick, error, required = true, disabled = false,
}: {
  label: string;
  value?: File | null;
  onPick: (file: File) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between gap-3">
        <span className={`text-sm font-semibold flex-1 ${disabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-200'}`}>
          {label}{required && ' *'}
        </span>
        <label className={`flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors flex-shrink-0 ${disabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#004275] hover:bg-[#003055] cursor-pointer'}`}>
          <FileText className="w-3.5 h-3.5" />
          Pick File
          <input type="file" disabled={disabled} className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); (e.target as HTMLInputElement).value = ''; }} />
        </label>
      </div>
      {value && <p className="text-xs font-semibold text-green-600 mt-1">✓ {value.name}</p>}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ─── Documentation Section Header ─────────────────────────────────────────────
function DocumentationHeader({ title }: { title: string }) {
  return <h3 className="text-base font-bold text-[#004275] dark:text-[#38bdf8] mt-5 mb-3 border-b border-[#004275]/20 dark:border-[#38bdf8]/20 pb-1">{title}</h3>;
}

// ─── Toggle Switch ─────────────────────────────────────────────────────────────
function ToggleSwitch({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-[#004275]' : 'bg-gray-300 dark:bg-gray-600'}`}>
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

// ─── Map Component ─────────────────────────────────────────────────────────────
function MapComponent({ coordinates, onGetLocation, isGettingLocation, onChangeCoordinates }: {
  coordinates: { lat: number; lng: number } | null;
  onGetLocation: () => void;
  isGettingLocation: boolean;
  onChangeCoordinates: (coords: { lat: number; lng: number }) => void;
}) {
  const mapContainerId = "leaflet-admin-map-picker";
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const coordsRef = useRef(coordinates);

  useEffect(() => {
    coordsRef.current = coordinates;
  }, [coordinates]);

  useEffect(() => {
    // Dynamically insert Leaflet stylesheet if not present
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    // Dynamically load Leaflet script
    const loadLeaflet = () => {
      return new Promise<any>((resolve) => {
        if ((window as any).L) {
          resolve((window as any).L);
          return;
        }
        const script = document.createElement("script");
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.onload = () => resolve((window as any).L);
        document.body.appendChild(script);
      });
    };

    loadLeaflet().then((L) => {
      const container = document.getElementById(mapContainerId);
      if (!container) return;

      const activeCoords = coordsRef.current || { lat: 18.5204, lng: 73.8567 };

      // Initialize map
      const mapInstance = L.map(container).setView([activeCoords.lat, activeCoords.lng], 15);
      mapRef.current = mapInstance;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(mapInstance);

      // Create draggable marker
      const markerInstance = L.marker([activeCoords.lat, activeCoords.lng], {
        draggable: true
      }).addTo(mapInstance);
      markerRef.current = markerInstance;

      // Drag event handler
      markerInstance.on("dragend", () => {
        const position = markerInstance.getLatLng();
        onChangeCoordinates({ lat: position.lat, lng: position.lng });
      });

      // Map click handler to move marker
      mapInstance.on("click", (e: any) => {
        const position = e.latlng;
        markerInstance.setLatLng(position);
        onChangeCoordinates({ lat: position.lat, lng: position.lng });
      });
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, []);

  // Update map view & marker when external coordinates change
  useEffect(() => {
    if (mapRef.current && markerRef.current && coordinates) {
      mapRef.current.setView([coordinates.lat, coordinates.lng], 15);
      markerRef.current.setLatLng([coordinates.lat, coordinates.lng]);
    }
  }, [coordinates]);

  return (
    <div className="space-y-3">
      <button type="button" onClick={onGetLocation} disabled={isGettingLocation}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium">
        <Navigation className="w-4 h-4" />
        {isGettingLocation ? 'Getting Location...' : 'Get Current Location'}
      </button>
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden relative">
        <div id={mapContainerId} style={{ width: "100%", height: "250px" }} />
      </div>
    </div>
  );
}

const Divider = () => <hr className="border-gray-200 dark:border-gray-700 my-5" />;

// ─── FIELD_METADATA for step-jump ─────────────────────────────────────────────
const FIELD_METADATA: Record<string, { step: number }> = {
  // Step 0
  doctorName: { step: 0 },
  category: { step: 0 },
  otherCategory: { step: 0 },
  clinicName: { step: 0 },
  email: { step: 0 },
  gender: { step: 0 },
  phoneNumber: { step: 0 },
  profilePicture: { step: 0 },

  // Step 1
  experienceYears: { step: 1 },
  educationalQualifications: { step: 1 },
  specializations: { step: 1 },
  languageProficiency: { step: 1 },

  // Step 2
  licenseNumber: { step: 2 },
  registrationCouncil: { step: 2 },
  otherRegistrationCouncil: { step: 2 },
  registrationDate: { step: 2 },
  registrationExpiryDate: { step: 2 },
  identityProofType: { step: 2 },
  identityProofIssueDate: { step: 2 },
  identityProofExpiryDate: { step: 2 },
  identityProofFront: { step: 2 },
  identityProofBack: { step: 2 },
  medicalRegistration: { step: 2 },

  // Step 3
  consultationTypes: { step: 3 },
  consultationDays: { step: 3 },
  specialityFeesList: { step: 3 },
  establishmentRole: { step: 3 },
  establishmentProof: { step: 3 },

  // Step 4
  address: { step: 4 },
  state: { step: 4 },
  city: { step: 4 },
  otherCity: { step: 4 },
  pincode: { step: 4 },
  floor: { step: 4 },
  otherFloorNumber: { step: 4 },
  locationLink: { step: 4 },
  clinicPhotos: { step: 4 },
  patientContactNumber: { step: 4 },
  description: { step: 4 },

  bankAccountHolderName: { step: 4 },
  bankAccountNumber: { step: 4 },
  bankIfscCode: { step: 4 },
  bankName: { step: 4 },
  bankBranchName: { step: 4 },
  bankAccountType: { step: 4 },
  isDeclarationAccepted: { step: 4 },
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DoctorClinicVendorForm: React.FC = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<Record<string, any>>({
    doctorName: 'Dr. ', category: '', otherCategory: '', clinicName: '', email: '', gender: '', phoneNumber: '',
    experienceYears: '', educationalQualifications: [], otherQualification: '',
    specializations: [], otherSpecialization: '',
    languageProficiency: [], otherLanguage: '',
    licenseNumber: '', registrationCouncil: '', otherRegistrationCouncil: '', registrationDate: '', registrationExpiryDate: '',
    identityProofType: '', identityProofIssueDate: '', identityProofExpiryDate: '',
    degreeCertificates: {}, specializationCertificates: {},
    consultationTypes: [], consultationDays: [], specialityFeesList: [],
    hasTelemedicineExperience: false,
    establishmentRole: 'Owner of establishment', establishmentProofType: '', customEstablishmentProofName: '',
    address: '', state: '', city: '', otherCity: '', pincode: '', nearbyLandmark: '', floor: '', otherFloorNumber: '',
    hasLiftAccess: false, hasWheelchairAccess: false, hasParking: false,
    description: '', patientContactNumber: '', locationLink: '',
    bankAccountHolderName: '', bankAccountNumber: '', bankIfscCode: '', bankName: '', bankBranchName: '', bankAccountType: 'Savings',
    isDeclarationAccepted: false,
  });

  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [profilePic, setProfilePic] = useState<any>(null);
  const [identityProofFront, setIdentityProofFront] = useState<any>(null);
  const [identityProofBack, setIdentityProofBack] = useState<any>(null);
  const [medicalReg, setMedicalReg] = useState<any>(null);
  const [establishmentProof, setEstablishmentProof] = useState<any>(null);

  const [degreeCerts, setDegreeCerts] = useState<Record<string, any>>({});
  const [specializationCerts, setSpecializationCerts] = useState<Record<string, any>>({});
  const [clinicPhotos, setClinicPhotos] = useState<Array<{ file: File; preview: string }>>([]);

  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [rawFieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [showStepErrors, setShowStepErrors] = useState<Record<number, boolean>>({});

  const fieldErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    Object.keys(rawFieldErrors).forEach(k => {
      const meta = FIELD_METADATA[k] ?? (k.startsWith('degree_') || k.startsWith('specialization_') ? { step: 2 } : null);
      const fieldStep = meta ? meta.step : -1;

      // Final step:
      // show individual errors only after field interaction,
      // or show all after final Submit is attempted.
      if (fieldStep === 4) {
        if (touchedFields[k] || showStepErrors[4]) {
          errs[k] = rawFieldErrors[k];
        }
        return;
      }

      // Previous steps:
      if (showStepErrors[fieldStep] || touchedFields[k]) {
        errs[k] = rawFieldErrors[k];
      }
    });
    return errs;
  }, [rawFieldErrors, showStepErrors, touchedFields]);
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState<string | null>(null);
  const [imageScale, setImageScale] = useState(1);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [timer, setTimer] = useState(0);
  const [bankIfscLoading, setBankIfscLoading] = useState(false);
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [mapSearchText, setMapSearchText] = useState('');
  const [mapSearchLoading, setMapSearchLoading] = useState(false);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastStepChangeTimeRef = useRef<number>(Date.now());

  const reverseGeocodeLat = async (lat: number, lng: number) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&email=admin@vedika.tech`);
        const data = await res.json();
        if (data && data.address) {
          const addr = data.address;
          const cityName = addr.city || addr.town || addr.village || addr.suburb || addr.county || '';
          const stateName = addr.state || '';
          const displayName = data.display_name || '';
          if (displayName) {
            setFormField('locationLink', displayName);
            if (!form.address) setFormField('address', displayName);
            setMapSearchText(displayName);
          }
          if (stateName) {
            const matchState = stateCityList.find((s: any) => s.state.toLowerCase() === stateName.toLowerCase());
            if (matchState) {
              setSelectedState(matchState.state);
              setFormField('state', matchState.state);
              setAvailableCities(matchState.cities);
              if (cityName) {
                const matchCity = matchState.cities.find((c: string) => c.toLowerCase() === cityName.toLowerCase());
                if (matchCity) setFormField('city', matchCity);
              }
            }
          }
        }
      } catch (e) {
        console.warn('Reverse geocoding failed:', e);
      }
    }, 300);
  };

  // Eager geolocation & map initialization when dialog opens
  useEffect(() => {
    if (showMapDialog && !coordinates) {
      if (navigator.geolocation) {
        setIsGettingLocation(true);
        navigator.geolocation.getCurrentPosition(
          ({ coords: { latitude, longitude } }) => {
            setCoordinates({ lat: latitude, lng: longitude });
            reverseGeocodeLat(latitude, longitude);
            setIsGettingLocation(false);
          },
          () => setIsGettingLocation(false),
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
        );
      }
    }
  }, [showMapDialog, coordinates]);

  const handleMapSearch = async (text: string) => {
    if (!text.trim()) return;
    setMapSearchLoading(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(text)}&limit=1&email=admin@vedika.tech`);
      const data = await res.json();
      if (data && data.length > 0) {
        const item = data[0];
        const lat = parseFloat(item.lat);
        const lng = parseFloat(item.lon);
        setCoordinates({ lat, lng });
        if (item.display_name) {
          setFormField('locationLink', item.display_name);
          setMapSearchText(item.display_name);
        }
        await reverseGeocodeLat(lat, lng);
      }
    } catch (e) {
      console.warn('Geocoding search failed:', e);
    } finally {
      setMapSearchLoading(false);
    }
  };

  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const checkContactAvailability = async (type: 'email' | 'phoneNumber', value: string): Promise<boolean> => {
    if (!value) return true;
    try {
      const payload: Record<string, any> = {};
      payload[type] = value;
      if (isEditMode) {
        payload.excludeVendorId = vendorId;
      }
      const res = await api.post('/vendors/contact/check-availability', payload);
      return res.data?.available !== false;
    } catch (e: any) {
      if (e?.response?.data?.available === false) {
        return false;
      }
      return true;
    }
  };
  const { useAuth: _auth } = useAuth() as any; // Safe cast
  const { vendorId: urlVendorId } = useParams<{ vendorId: string }>();
  const routeLocation = useLocation();
  const navigate = useNavigate();

  const vendorData = routeLocation.state?.vendorData;
  const vendorId = urlVendorId || vendorData?.vendor?.vendorId || vendorData?.fullProfile?.vendorId;
  const isEditMode = !!vendorId;

  // Determine environment
  const isDevelopment = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );

  // OTP timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) interval = setInterval(() => setTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  // Restore draft
  useEffect(() => {
    if (!isEditMode) {
      try {
        const saved = localStorage.getItem('vendorClinicRegistrationDraft_v1');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && typeof parsed === 'object') {
            setForm(prev => ({ ...prev, ...parsed }));
            if (parsed.state) {
              setSelectedState(parsed.state);
              const found = stateCityList.find((s: any) => s.state === parsed.state);
              setAvailableCities(found ? found.cities : []);
            }
          }
        }
      } catch { }
    }
  }, [isEditMode]);

  // Auto-save draft
  useEffect(() => {
    if (!isEditMode && Object.keys(form).length > 0) {
      try { localStorage.setItem('vendorClinicRegistrationDraft_v1', JSON.stringify(form)); } catch { }
    }
  }, [form, isEditMode]);

  // Populate edit mode
  useEffect(() => {
    if (isEditMode && vendorData?.fullProfile) {
      const p = vendorData.fullProfile;
      setForm((prev: any) => ({
        ...prev,
        doctorName: p.doctorName || 'Dr. ',
        category: p.category || '',
        clinicName: p.clinicName || '',
        email: p.email || '',
        gender: p.gender || '',
        phoneNumber: p.phoneNumber || '',
        experienceYears: p.experienceYears || '',
        educationalQualifications: Array.isArray(p.educationalQualifications) ? p.educationalQualifications.map((q: any) => typeof q === 'object' && q.degree ? q.degree : q) : [],
        specializations: p.specializations || [],
        languageProficiency: p.languageProficiency || [],
        licenseNumber: p.licenseNumber || '',
        registrationCouncil: p.registrationCouncil || '',
        registrationDate: p.registrationDate || '',
        registrationExpiryDate: p.registrationExpiryDate || '',
        identityProofType: p.identityProofType || '',
        consultationTypes: Array.isArray(p.consultationTypes) ? p.consultationTypes.map((t: string) => t === 'Offline' ? 'Offline (In-Clinic)' : t) : [],
        consultationDays: p.consultationDays || [],
        establishmentRole: p.establishmentRole || 'Owner of establishment',
        address: p.address || '',
        state: p.state || '',
        city: p.city || '',
        pincode: p.pincode || '',
        nearbyLandmark: p.nearbyLandmark || p.landmark || '',
        floor: p.floor || '',
        hasLiftAccess: p.hasLiftAccess || false,
        hasWheelchairAccess: p.hasWheelchairAccess || false,
        hasParking: p.hasParking || false,
        description: p.description || '',
        patientContactNumber: p.patientContactNumber || '',
        bankAccountHolderName: p.bankAccountHolderName || '',
        bankAccountNumber: p.bankAccountNumber || '',
        bankIfscCode: p.bankIfscCode || '',
        bankName: p.bankName || '',
        bankBranchName: p.bankBranchName || '',
        bankAccountType: p.bankAccountType || 'Savings',
        isDeclarationAccepted: p.isDeclarationAccepted || false,
      }));
      if (p.state) {
        setSelectedState(p.state);
        const found = stateCityList.find((s: any) => s.state === p.state);
        setAvailableCities(found ? found.cities : []);
      }
      if (p.location) {
        const [lat, lng] = p.location.split(',').map((c: string) => parseFloat(c.trim()));
        if (!isNaN(lat) && !isNaN(lng)) setCoordinates({ lat, lng });
      }
      if (p.profilePicture) {
        setProfilePic({ name: 'profile_pic', preview: p.profilePicture });
      }
      if (p.identityProofFront) {
        setIdentityProofFront({ name: 'identity_front', preview: p.identityProofFront });
      }
      if (p.identityProofBack) {
        setIdentityProofBack({ name: 'identity_back', preview: p.identityProofBack });
      }
      if (p.medicalLicenseFile || p.medicalRegistration) {
        setMedicalReg({ name: 'registration_cert', preview: p.medicalLicenseFile || p.medicalRegistration });
      }
      if (p.establishmentProof) {
        setEstablishmentProof({ name: 'establishment_proof', preview: p.establishmentProof });
      }
    }
  }, [isEditMode, vendorData]);

  // Sync fee matrix rows when specializations list changes
  useEffect(() => {
    const specs = form.specializations || [];
    setForm(prev => {
      const currentList = prev.specialityFeesList || [];
      const updatedList = syncSpecialityFeeRows(specs, currentList);
      return { ...prev, specialityFeesList: updatedList };
    });
  }, [form.specializations]);

  const setFormField = (name: string, value: any) => {
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      return updated;
    });
    clearErrorOnValidInput(name, value);
  };

  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    navigator.geolocation?.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        setCoordinates({ lat: latitude, lng: longitude });
        setFormField('location', `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
        const formatted = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        setFormField('locationLink', formatted);
        await reverseGeocodeLat(latitude, longitude);
        setIsGettingLocation(false);
      },
      () => setIsGettingLocation(false),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // ─── Reactive validation rules ───────────────────────────────────────────────
  const validateField = (name: string, value: any): string | null => {
    if (name === 'doctorName') return validateDoctorName(value);
    if (name === 'category') return !value ? 'Category is required' : null;
    if (name === 'otherCategory' && form.category === 'Other') return !String(value || '').trim() ? 'Specify category is required' : null;
    if (name === 'clinicName') {
      const valStr = String(value || '').trim();
      if (valStr) {
        if (valStr.length < 3) return 'Clinic/Business Name must be at least 3 characters';
        if (!clinicRegex.test(valStr)) return 'Clinic/Business Name contains invalid characters';
      }
      return null;
    }
    if (name === 'email') {
      if (!String(value || '').trim()) return 'Email Address is required';
      if (!emailRegex.test(value)) return 'Enter a valid email address';
      return null;
    }
    if (name === 'gender') return !value ? 'Gender is required' : null;
    if (name === 'phoneNumber') {
      if (!value || String(value).replace(/\D/g, '').length !== 10) return 'Enter a valid 10-digit mobile number';
      return null;
    }
    if (name === 'experienceYears') {
      if (value === '' || value === null || value === undefined) return 'Experience is required';
      if (Number(value) > 99) return 'Experience must be below 100 years';
      return null;
    }
    if (name === 'educationalQualifications') return !value?.length ? 'Select at least one qualification' : null;
    if (name === 'specializations') return !value?.length ? 'Select at least one specialization' : null;
    if (name === 'languageProficiency') return !value?.length ? 'Select at least one language' : null;

    if (name === 'licenseNumber') return !String(value || '').trim() ? 'Medical Registration Number is required' : null;
    if (name === 'registrationCouncil') return !value ? 'Registration Council is required' : null;
    if (name === 'otherRegistrationCouncil' && form.registrationCouncil === 'Other') return !String(value || '').trim() ? 'Other council name is required' : null;

    if (name === 'registrationDate') {
      if (!value) return 'Registration Date is required';
      const selDate = new Date(value);
      selDate.setHours(0, 0, 0, 0);
      const minDate = new Date(MIN_REGISTRATION_DATE);
      minDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selDate < minDate) return 'Registration date must be from 1950 onwards';
      if (selDate >= today) return 'Registration date must be before today';
      return null;
    }
    if (name === 'registrationExpiryDate') {
      if (!value) return 'Medical Registration Expiry Date is required';
      const expiryDate = new Date(value);
      expiryDate.setHours(0, 0, 0, 0);
      const minExpiry = new Date();
      minExpiry.setDate(minExpiry.getDate() + 30);
      minExpiry.setHours(0, 0, 0, 0);
      if (expiryDate < minExpiry) return 'Expiry date must be at least 1 month from today';
      if (form.registrationDate && expiryDate <= new Date(form.registrationDate)) {
        return 'Expiry date must be after registration date';
      }
      return null;
    }
    if (name === 'identityProofType') return !value ? 'Identity Proof Type is required' : null;
    if (name === 'identityProofIssueDate' && form.identityProofType === 'Driving License') {
      if (!value) return 'Issue Date is required for Driving License';
      const selDate = new Date(value);
      selDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selDate >= today) return 'Issue date must be before today';
      return null;
    }
    if (name === 'identityProofExpiryDate' && form.identityProofType === 'Driving License') {
      if (!value) return 'Expiry Date is required';
      const expiryDate = new Date(value);
      expiryDate.setHours(0, 0, 0, 0);
      const minExpiry = new Date();
      minExpiry.setDate(minExpiry.getDate() + 30);
      minExpiry.setHours(0, 0, 0, 0);
      if (expiryDate < minExpiry) return 'Expiry date must be at least 1 month from today';
      if (form.identityProofIssueDate && expiryDate <= new Date(form.identityProofIssueDate)) {
        return 'Expiry date must be after issue date';
      }
      return null;
    }

    // Gated Address fields (only for offline)
    const isOffline = (form.consultationTypes || []).includes('Offline (In-Clinic)');
    if (isOffline) {
      if (name === 'address') return !String(value || '').trim() ? 'Full Address is required' : null;
      if (name === 'state') return !value ? 'State is required' : null;
      if (name === 'city') return !value ? 'City is required' : null;
      if (name === 'otherCity' && form.city === 'Other') return !String(value || '').trim() ? 'Specify city name is required' : null;
      if (name === 'pincode') return !/^\d{6}$/.test(String(value || '')) ? 'Enter a valid 6-digit pincode' : null;
      if (name === 'floor') return !value ? 'Floor is required' : null;
      if (name === 'otherFloorNumber' && form.floor === 'Other') return !String(value || '').trim() ? 'Specify floor number is required' : null;
      if (name === 'patientContactNumber') {
        const check = validateContactNumber(value);
        return !check.valid ? check.message : null;
      }
    }

    if (name === 'description') {
      if (!String(value || '').trim()) return 'About Yourself is required';
      if (String(value).length > 5000) return 'About Yourself must be up to 5000 characters';
      return null;
    }

    // Bank Details
    if (!isEditMode) {
      if (name === 'bankAccountHolderName') return !String(value || '').trim() ? 'Account holder name is required' : null;
      if (name === 'bankAccountNumber') return !String(value || '').trim() ? 'Account number is required' : null;
      if (name === 'bankIfscCode') return !isValidIfsc(value) ? 'Enter a valid IFSC code (e.g. HDFC0001234)' : null;
      if (name === 'bankName') return !String(value || '').trim() ? 'Bank name is required' : null;
      if (name === 'bankBranchName') return !String(value || '').trim() ? 'Branch name is required' : null;
    }

    return null;
  };

  const clearErrorOnValidInput = (name: string, value: any) => {
    const errorMsg = validateField(name, value);
    if (!errorMsg) {
      setFieldErrors(prev => {
        if (!prev[name]) return prev;
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    } else {
      setFieldErrors(prev => {
        if (prev[name]) return { ...prev, [name]: errorMsg };
        return prev;
      });
    }
  };

  const handleBlur = async (name: string) => {
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    let errorMsg = validateField(name, form[name]);
    if (!errorMsg) {
      if (name === 'email') {
        const available = await checkContactAvailability('email', form.email);
        if (!available) errorMsg = 'Email is already registered';
      } else if (name === 'phoneNumber') {
        const available = await checkContactAvailability('phoneNumber', form.phoneNumber);
        if (!available) errorMsg = 'Phone number is already registered';
      }
    }

    if (errorMsg) {
      setFieldErrors(prev => ({ ...prev, [name]: errorMsg! }));
    } else {
      setFieldErrors(prev => {
        if (!prev[name]) return prev;
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  // ─── Document Upload helper ──────────────────────────────────────────────────
  const handleDocumentPick = (field: string, file: File, keyName?: string) => {
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFieldErrors(prev => ({ ...prev, [keyName ? `${field}_${keyName}` : field]: 'File must be less than 5 MB' }));
      return;
    }
    if (!ALLOWED_DOC_TYPES.includes(file.type)) {
      setFieldErrors(prev => ({ ...prev, [keyName ? `${field}_${keyName}` : field]: 'Only JPG, PNG or PDF allowed' }));
      return;
    }

    const filePayload = { file, name: file.name, preview: URL.createObjectURL(file) };

    if (field === 'degreeCertificate' && keyName) {
      setDegreeCerts(prev => ({ ...prev, [keyName]: filePayload }));
      setFieldErrors(prev => { const c = { ...prev }; delete c[`degree_${keyName}`]; return c; });
    } else if (field === 'specializationCertificate' && keyName) {
      setSpecializationCerts(prev => ({ ...prev, [keyName]: filePayload }));
      setFieldErrors(prev => { const c = { ...prev }; delete c[`specialization_${keyName}`]; return c; });
    } else {
      if (field === 'profilePicture') {
        const imageErr = validateImageFile(file);
        if (imageErr) {
          setFieldErrors(prev => ({ ...prev, profilePicture: imageErr }));
          return;
        }
        setProfilePic(filePayload);
        setFieldErrors(prev => { const c = { ...prev }; delete c.profilePicture; return c; });
      } else if (field === 'identityProofFront') {
        setIdentityProofFront(filePayload);
        setFieldErrors(prev => { const c = { ...prev }; delete c.identityProofFront; return c; });
      } else if (field === 'identityProofBack') {
        setIdentityProofBack(filePayload);
        setFieldErrors(prev => { const c = { ...prev }; delete c.identityProofBack; return c; });
      } else if (field === 'medicalRegistration') {
        setMedicalReg(filePayload);
        setFieldErrors(prev => { const c = { ...prev }; delete c.medicalRegistration; return c; });
      } else if (field === 'establishmentProof') {
        setEstablishmentProof(filePayload);
        setFieldErrors(prev => { const c = { ...prev }; delete c.establishmentProof; return c; });
      }
    }
  };

  const handleClinicPhotoAdd = (files: FileList | null) => {
    if (!files) return;
    const filesArray = Array.from(files);
    const remainingSlots = 5 - clinicPhotos.length;
    if (remainingSlots <= 0) return;

    const filesToAdd = filesArray.slice(0, remainingSlots).map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setClinicPhotos(prev => [...prev, ...filesToAdd]);
    if (clinicPhotos.length + filesToAdd.length >= 3) {
      setFieldErrors(prev => { const c = { ...prev }; delete c.clinicPhotos; return c; });
    }
  };

  const removeClinicPhoto = (idx: number) => {
    setClinicPhotos(prev => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[idx].preview);
      copy.splice(idx, 1);
      return copy;
    });
  };

  // ─── Step Validation ────────────────────────────────────────────────────────
  const validateStep = (idx: number): boolean => {
    const errs: Record<string, string> = {};

    if (idx === 0) {
      if (!profilePic && !profilePreview) errs.profilePicture = 'Profile picture is required';
      if (!form.category) errs.category = 'Category is required';
      if (form.category === 'Other' && !form.otherCategory?.trim()) errs.otherCategory = 'Please specify category';
      const nameErr = validateDoctorName(form.doctorName || '');
      if (nameErr) errs.doctorName = nameErr;
      if (!form.email?.trim()) errs.email = 'Email Address is required';
      else if (!emailRegex.test(form.email)) errs.email = 'Enter a valid email address';
      else if (rawFieldErrors.email === 'Email is already registered') {
        errs.email = 'Email is already registered';
      }
      if (!form.gender) errs.gender = 'Gender is required';
      const isDevTestNumber = isDevelopment && (
        form.phoneNumber?.startsWith('1234678') ||
        form.phoneNumber?.startsWith('12345678')
      );
      if (!form.phoneNumber || (!isDevTestNumber && (form.phoneNumber.length !== 10 || !/^[6-9]\d{9}$/.test(form.phoneNumber)))) {
        errs.phoneNumber = 'Enter a valid 10-digit mobile number';
      } else if (rawFieldErrors.phoneNumber === 'Phone number is already registered') {
        errs.phoneNumber = 'Phone number is already registered';
      }
    }

    if (idx === 1) {
      if (!form.experienceYears && form.experienceYears !== 0) errs.experienceYears = 'Experience is required';
      else if (Number(form.experienceYears) > 99) errs.experienceYears = 'Experience must be below 100 years';
      if (!form.educationalQualifications?.length) {
        errs.educationalQualifications = 'Select at least one qualification';
      } else if (form.educationalQualifications?.includes('Other')) {
        const hasCustom = form.educationalQualifications.some((v: string) => v.startsWith('Other:'));
        if (!hasCustom) errs.educationalQualifications = 'Please add at least one custom qualification';
      }
      if (!form.specializations?.length) {
        errs.specializations = 'Select at least one specialization';
      } else if (form.specializations?.includes('Other')) {
        const hasCustom = form.specializations.some((v: string) => v.startsWith('Other:'));
        if (!hasCustom) errs.specializations = 'Please add at least one custom specialization';
      }
      if (!form.languageProficiency?.length) errs.languageProficiency = 'Select at least one language';
    }

    if (idx === 2) {
      if (!form.licenseNumber?.trim()) errs.licenseNumber = 'Medical Registration Number is required';
      if (!form.registrationCouncil) errs.registrationCouncil = 'Registration Council is required';
      if (form.registrationCouncil === 'Other' && !form.otherRegistrationCouncil?.trim()) {
        errs.otherRegistrationCouncil = 'Other council name is required';
      }
      if (!form.registrationDate) {
        errs.registrationDate = 'Registration Date is required';
      } else {
        const selDate = new Date(form.registrationDate);
        selDate.setHours(0, 0, 0, 0);
        const minDate = new Date(MIN_REGISTRATION_DATE);
        minDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selDate < minDate) errs.registrationDate = 'Registration date must be from 1950 onwards';
        else if (selDate >= today) errs.registrationDate = 'Registration date must be before today';
      }
      if (!form.registrationExpiryDate) {
        errs.registrationExpiryDate = 'Medical Registration Expiry Date is required';
      } else {
        const minExpiry = new Date();
        minExpiry.setDate(minExpiry.getDate() + 30);
        minExpiry.setHours(0, 0, 0, 0);
        const expiryDate = new Date(form.registrationExpiryDate);
        expiryDate.setHours(0, 0, 0, 0);
        if (expiryDate < minExpiry) {
          errs.registrationExpiryDate = 'Expiry date must be at least 1 month from today';
        } else if (form.registrationDate && expiryDate <= new Date(form.registrationDate)) {
          errs.registrationExpiryDate = 'Expiry date must be after registration date';
        }
      }
      if (!form.identityProofType) errs.identityProofType = 'Identity Proof Type is required';
      if (form.identityProofType === 'Driving License') {
        if (!form.identityProofIssueDate) {
          errs.identityProofIssueDate = 'Issue Date is required for Driving License';
        } else {
          const selDate = new Date(form.identityProofIssueDate);
          selDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selDate >= today) {
            errs.identityProofIssueDate = 'Issue date must be before today';
          }
        }
        if (!form.identityProofExpiryDate) {
          errs.identityProofExpiryDate = 'Expiry Date is required for Driving License';
        } else {
          const minExpiry = new Date();
          minExpiry.setDate(minExpiry.getDate() + 30);
          minExpiry.setHours(0, 0, 0, 0);
          if (new Date(form.identityProofExpiryDate) < minExpiry) {
            errs.identityProofExpiryDate = 'Expiry date must be at least 1 month from today';
          } else if (form.identityProofIssueDate && new Date(form.identityProofExpiryDate) <= new Date(form.identityProofIssueDate)) {
            errs.identityProofExpiryDate = 'Expiry date must be after issue date';
          }
        }
      }
      if (!identityProofFront) errs.identityProofFront = 'Identity proof front side is required';
      if (identityRequiresBack(form.identityProofType) && !identityProofBack) {
        errs.identityProofBack = `Identity proof back side is required for ${form.identityProofType}`;
      }
      if (!medicalReg) errs.medicalRegistration = 'Medical Registration Certificate is required';

      const quals: string[] = (form.educationalQualifications || []).filter((q: string) => q !== 'Other' && !q.startsWith('Other:'));
      quals.forEach((q: string) => {
        if (!degreeCerts[q]) errs[`degree_${q}`] = `${q} degree certificate is required`;
      });
    }

    if (idx === 3) {
      if (!form.consultationTypes?.length) errs.consultationTypes = 'Select at least one consultation type';
      if (!form.consultationDays?.length) errs.consultationDays = 'Select at least one consultation day';

      if (requiresSpecialityWiseFees(form.consultationTypes)) {
        const { needOnline, needOffline } = feeChannelNeeds(form.consultationTypes);
        const specs = specsEligibleForFees(form.specializations);
        if (specs.length === 0) {
          errs.specialityFeesList = 'Add at least one specialization in the previous step to enter fees';
        } else {
          const rows = form.specialityFeesList || [];
          const incomplete = rows.some((row: any) => !feeRowComplete(row, needOnline, needOffline));
          const exceeds = rows.some((row: any) => {
            const btc = row.feesByConsultationType || {};
            if (needOnline && (Number(btc.online?.firstVisitFees || 0) > 10000 || Number(btc.online?.followUpFees || 0) > 10000)) return true;
            if (needOffline && (Number(btc.offline?.firstVisitFees || 0) > 10000 || Number(btc.offline?.followUpFees || 0) > 10000)) return true;
            return false;
          });
          if (incomplete) {
            errs.specialityFeesList = 'Enter all required first visit and follow-up amounts for each specialization';
          } else if (exceeds) {
            errs.specialityFeesList = 'Consultation fees cannot exceed ₹10,000';
          }
        }
      }

      if (form.establishmentRole && !establishmentProof) {
        errs.establishmentProof = 'Business Proof document is required';
      }
    }

    if (idx === 4) {
      const isOffline = (form.consultationTypes || []).includes('Offline (In-Clinic)');
      if (isOffline) {
        if (!form.address?.trim()) errs.address = 'Full Address is required';
        if (!selectedState) errs.state = 'State is required';
        if (!form.city) errs.city = 'City is required';
        if (form.city === 'Other' && !form.otherCity?.trim()) errs.otherCity = 'Please specify city name';
        if (!form.pincode || !/^\d{6}$/.test(String(form.pincode))) errs.pincode = 'Enter a valid 6-digit pincode';
        if (!form.floor) errs.floor = 'Floor is required';
        if (form.floor === 'Other' && !form.otherFloorNumber?.trim()) errs.otherFloorNumber = 'Please enter floor number';
        if (!form.locationLink?.trim()) errs.locationLink = 'Please pin your location on the map';
        if (clinicPhotos.length < 3) errs.clinicPhotos = 'At least 3 clinic photos are required for offline consultation';
        if (form.patientContactNumber?.trim()) {
          const contactValidation = validateContactNumber(form.patientContactNumber);
          if (!contactValidation.valid) errs.patientContactNumber = contactValidation.message;
        } else {
          errs.patientContactNumber = 'Clinic Contact Number is required for offline consultation';
        }
      }
      if (!form.description?.trim()) errs.description = 'About Yourself is required';
      else if (form.description.length > 5000) errs.description = 'About Yourself must be up to 5000 characters';

      if (!isEditMode) {
        if (!String(form.bankAccountHolderName || '').trim()) errs.bankAccountHolderName = 'Account holder name is required';
        if (!String(form.bankAccountNumber || '').trim()) errs.bankAccountNumber = 'Account number is required';
        if (!String(form.bankIfscCode || '').trim() || !isValidIfsc(form.bankIfscCode || '')) {
          errs.bankIfscCode = 'Enter a valid IFSC code (e.g. HDFC0001234)';
        }
        if (!String(form.bankName || '').trim()) errs.bankName = 'Bank name is required';
        if (!String(form.bankBranchName || '').trim()) errs.bankBranchName = 'Branch name is required';
        const accType = String(form.bankAccountType || '').trim().toUpperCase();
        if (!accType || !['SAVINGS', 'CURRENT'].includes(accType)) errs.bankAccountType = 'Select account type (Savings/Current)';
      }

      if (!form.isDeclarationAccepted) {
        errs.isDeclarationAccepted = 'You must accept the declaration to submit';
      }
    }

    setFieldErrors(prev => {
      const copy = { ...prev };
      Object.keys(copy).forEach(k => {
        const meta = FIELD_METADATA[k] ?? (k.startsWith('degree_') || k.startsWith('specialization_') ? { step: 2 } : null);
        if (meta && meta.step === idx) delete copy[k];
      });
      return { ...copy, ...errs };
    });
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      setTimeout(() => {
        const el = fieldRefs.current[firstKey];
        el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
    return Object.keys(errs).length === 0;
  };

  const validateAllSteps = (): boolean => {
    const errs: Record<string, string> = {};

    // Step 0
    if (!profilePic && !profilePreview) errs.profilePicture = 'Profile picture is required';
    if (!form.category) errs.category = 'Category is required';
    if (form.category === 'Other' && !form.otherCategory?.trim()) errs.otherCategory = 'Please specify category';
    const nameErr = validateDoctorName(form.doctorName || '');
    if (nameErr) errs.doctorName = nameErr;
    if (!form.email?.trim()) errs.email = 'Email Address is required';
    else if (!emailRegex.test(form.email)) errs.email = 'Enter a valid email address';
    if (!form.gender) errs.gender = 'Gender is required';
    const isDevTestNumber = isDevelopment && (
      form.phoneNumber?.startsWith('1234678') ||
      form.phoneNumber?.startsWith('12345678')
    );
    if (!form.phoneNumber || (!isDevTestNumber && (form.phoneNumber.length !== 10 || !/^[6-9]\d{9}$/.test(form.phoneNumber)))) {
      errs.phoneNumber = 'Enter a valid 10-digit mobile number';
    }

    // Step 1
    if (!form.experienceYears && form.experienceYears !== 0) errs.experienceYears = 'Experience is required';
    else if (Number(form.experienceYears) > 99) errs.experienceYears = 'Experience must be below 100 years';
    if (!form.educationalQualifications?.length) {
      errs.educationalQualifications = 'Select at least one qualification';
    } else if (form.educationalQualifications?.includes('Other')) {
      const hasCustom = form.educationalQualifications.some((v: string) => v.startsWith('Other:'));
      if (!hasCustom) errs.educationalQualifications = 'Please add at least one custom qualification';
    }
    if (!form.specializations?.length) {
      errs.specializations = 'Select at least one specialization';
    } else if (form.specializations?.includes('Other')) {
      const hasCustom = form.specializations.some((v: string) => v.startsWith('Other:'));
      if (!hasCustom) errs.specializations = 'Please add at least one custom specialization';
    }
    if (!form.languageProficiency?.length) errs.languageProficiency = 'Select at least one language';

    // Step 2
    if (!form.licenseNumber?.trim()) errs.licenseNumber = 'Medical Registration Number is required';
    if (!form.registrationCouncil) errs.registrationCouncil = 'Registration Council is required';
    if (form.registrationCouncil === 'Other' && !form.otherRegistrationCouncil?.trim()) {
      errs.otherRegistrationCouncil = 'Other council name is required';
    }
    if (!form.registrationDate) {
      errs.registrationDate = 'Registration Date is required';
    } else {
      const selDate = new Date(form.registrationDate);
      selDate.setHours(0, 0, 0, 0);
      const minDate = new Date(MIN_REGISTRATION_DATE);
      minDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selDate < minDate) errs.registrationDate = 'Registration date must be from 1950 onwards';
      else if (selDate >= today) errs.registrationDate = 'Registration date must be before today';
    }
    if (!form.registrationExpiryDate) {
      errs.registrationExpiryDate = 'Medical Registration Expiry Date is required';
    } else {
      const minExpiry = new Date();
      minExpiry.setDate(minExpiry.getDate() + 30);
      minExpiry.setHours(0, 0, 0, 0);
      const expiryDate = new Date(form.registrationExpiryDate);
      expiryDate.setHours(0, 0, 0, 0);
      if (expiryDate < minExpiry) {
        errs.registrationExpiryDate = 'Expiry date must be at least 1 month from today';
      } else if (form.registrationDate && expiryDate <= new Date(form.registrationDate)) {
        errs.registrationExpiryDate = 'Expiry date must be after registration date';
      }
    }
    if (!form.identityProofType) errs.identityProofType = 'Identity Proof Type is required';
    if (form.identityProofType === 'Driving License') {
      if (!form.identityProofIssueDate) {
        errs.identityProofIssueDate = 'Issue Date is required for Driving License';
      } else {
        const selDate = new Date(form.identityProofIssueDate);
        selDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selDate >= today) {
          errs.identityProofIssueDate = 'Issue date must be before today';
        }
      }
      if (!form.identityProofExpiryDate) {
        errs.identityProofExpiryDate = 'Expiry Date is required for Driving License';
      } else {
        const minExpiry = new Date();
        minExpiry.setDate(minExpiry.getDate() + 30);
        minExpiry.setHours(0, 0, 0, 0);
        if (new Date(form.identityProofExpiryDate) < minExpiry) {
          errs.identityProofExpiryDate = 'Expiry date must be at least 1 month from today';
        } else if (form.identityProofIssueDate && new Date(form.identityProofExpiryDate) <= new Date(form.identityProofIssueDate)) {
          errs.identityProofExpiryDate = 'Expiry date must be after issue date';
        }
      }
    }
    if (!identityProofFront) errs.identityProofFront = 'Identity proof front side is required';
    if (identityRequiresBack(form.identityProofType) && !identityProofBack) {
      errs.identityProofBack = `Identity proof back side is required for ${form.identityProofType}`;
    }
    if (!medicalReg) errs.medicalRegistration = 'Medical Registration Certificate is required';

    const quals: string[] = (form.educationalQualifications || []).filter((q: string) => q !== 'Other' && !q.startsWith('Other:'));
    quals.forEach((q: string) => {
      if (!degreeCerts[q]) errs[`degree_${q}`] = `${q} degree certificate is required`;
    });

    // Step 3
    if (!form.consultationTypes?.length) errs.consultationTypes = 'Select at least one consultation type';
    if (!form.consultationDays?.length) errs.consultationDays = 'Select at least one consultation day';

    if (requiresSpecialityWiseFees(form.consultationTypes)) {
      const { needOnline, needOffline } = feeChannelNeeds(form.consultationTypes);
      const specs = specsEligibleForFees(form.specializations);
      if (specs.length === 0) {
        errs.specialityFeesList = 'Add at least one specialization in the previous step to enter fees';
      } else {
        const rows = form.specialityFeesList || [];
        const incomplete = rows.some((row: any) => !feeRowComplete(row, needOnline, needOffline));
        const exceeds = rows.some((row: any) => {
          const btc = row.feesByConsultationType || {};
          if (needOnline && (Number(btc.online?.firstVisitFees || 0) > 10000 || Number(btc.online?.followUpFees || 0) > 10000)) return true;
          if (needOffline && (Number(btc.offline?.firstVisitFees || 0) > 10000 || Number(btc.offline?.followUpFees || 0) > 10000)) return true;
          return false;
        });
        if (incomplete) {
          errs.specialityFeesList = 'Enter all required first visit and follow-up amounts for each specialization';
        } else if (exceeds) {
          errs.specialityFeesList = 'Consultation fees cannot exceed ₹10,000';
        }
      }
    }

    if (form.establishmentRole && !establishmentProof) {
      errs.establishmentProof = 'Business Proof document is required';
    }

    // Step 4
    const isOffline = (form.consultationTypes || []).includes('Offline (In-Clinic)');
    if (isOffline) {
      if (!form.address?.trim()) errs.address = 'Full Address is required';
      if (!selectedState) errs.state = 'State is required';
      if (!form.city) errs.city = 'City is required';
      if (form.city === 'Other' && !form.otherCity?.trim()) errs.otherCity = 'Please specify city name';
      if (!form.pincode || !/^\d{6}$/.test(String(form.pincode))) errs.pincode = 'Enter a valid 6-digit pincode';
      if (!form.floor) errs.floor = 'Floor is required';
      if (form.floor === 'Other' && !form.otherFloorNumber?.trim()) errs.otherFloorNumber = 'Please enter floor number';
      if (!form.locationLink?.trim()) errs.locationLink = 'Please pin your location on the map';
      if (clinicPhotos.length < 3) errs.clinicPhotos = 'At least 3 clinic photos are required for offline consultation';
      if (form.patientContactNumber?.trim()) {
        const contactValidation = validateContactNumber(form.patientContactNumber);
        if (!contactValidation.valid) errs.patientContactNumber = contactValidation.message;
      } else {
        errs.patientContactNumber = 'Clinic Contact Number is required for offline consultation';
      }
    }
    if (!form.description?.trim()) errs.description = 'About Yourself is required';
    else if (form.description.length > 5000) errs.description = 'About Yourself must be up to 5000 characters';

    if (!isEditMode) {
      if (!String(form.bankAccountHolderName || '').trim()) errs.bankAccountHolderName = 'Account holder name is required';
      if (!String(form.bankAccountNumber || '').trim()) errs.bankAccountNumber = 'Account number is required';
      if (!String(form.bankIfscCode || '').trim() || !isValidIfsc(form.bankIfscCode || '')) {
        errs.bankIfscCode = 'Enter a valid IFSC code (e.g. HDFC0001234)';
      }
      if (!String(form.bankName || '').trim()) errs.bankName = 'Bank name is required';
      if (!String(form.bankBranchName || '').trim()) errs.bankBranchName = 'Branch name is required';
      const accType = String(form.bankAccountType || '').trim().toUpperCase();
      if (!accType || !['SAVINGS', 'CURRENT'].includes(accType)) errs.bankAccountType = 'Select account type (Savings/Current)';
    } setFieldErrors(errs);

    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const meta = FIELD_METADATA[firstKey] ?? (firstKey.startsWith('degree_') || firstKey.startsWith('specialization_') ? { step: 2 } : null);
      if (meta) {
        setStep(meta.step);
        setTimeout(() => {
          const el = fieldRefs.current[firstKey];
          el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
      return false;
    }
    return true;
  };

  const filterErrorsForStep = (currentErrors: Record<string, string>, targetStep: number) => {
    const nextErrors: Record<string, string> = {};
    Object.keys(currentErrors).forEach((key) => {
      const meta = FIELD_METADATA[key] ?? (key.startsWith('degree_') || key.startsWith('specialization_') ? { step: 2 } : null);
      if (meta && meta.step === targetStep) {
        nextErrors[key] = currentErrors[key];
      }
    });
    return nextErrors;
  };

  const nextStep = async () => {
    if (step >= STEPS.length - 1) return;
    const currentStep = step;

    // User explicitly attempted to leave THIS step.
    setShowStepErrors(prev => ({
      ...prev,
      [currentStep]: true,
    }));

    let isStepValid = validateStep(currentStep);

    if (currentStep === 0 && isStepValid) {
      const emailOk = await checkContactAvailability('email', form.email);
      const phoneOk = await checkContactAvailability('phoneNumber', form.phoneNumber);

      if (!emailOk) {
        setFieldErrors(prev => ({
          ...prev,
          email: 'Email is already registered',
        }));
        isStepValid = false;
      }

      if (!phoneOk) {
        setFieldErrors(prev => ({
          ...prev,
          phoneNumber: 'Phone number is already registered',
        }));
        isStepValid = false;
      }
    }

    if (!isStepValid) return;

    const nextStepIndex = Math.min(
      currentStep + 1,
      STEPS.length - 1
    );

    setShowStepErrors(prev => ({
      ...prev,
      [nextStepIndex]: false,
    }));

    setTouchedFields(prev => {
      const next = { ...prev };

      Object.keys(next).forEach(key => {
        const meta =
          FIELD_METADATA[key] ??
          (key.startsWith('degree_') || key.startsWith('specialization_')
            ? { step: 2 }
            : null);

        if (meta?.step === nextStepIndex) {
          delete next[key];
        }
      });

      return next;
    });

    lastStepChangeTimeRef.current = Date.now();
    setStep(nextStepIndex);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const prevStep = () => {
    setFieldErrors(prev => filterErrorsForStep(prev, step - 1));
    setTouchedFields(prev => {
      const copy = { ...prev };
      Object.keys(FIELD_METADATA).forEach(k => {
        if (FIELD_METADATA[k].step === step) delete copy[k];
      });
      if (step === 2) {
        Object.keys(copy).forEach(k => {
          if (k.startsWith('degree_') || k.startsWith('specialization_')) delete copy[k];
        });
      }
      return copy;
    });
    setShowStepErrors(prev => {
      const copy = { ...prev };
      delete copy[step];
      return copy;
    });
    lastStepChangeTimeRef.current = Date.now();
    setStep(s => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ─── IFSC Verification Flow ──────────────────────────────────────────────────
  const verifyIfsc = async (ifsc: string) => {
    const code = String(ifsc || '').trim().toUpperCase();
    if (code.length !== 11) return;
    if (!isValidIfsc(code)) {
      setFieldErrors(prev => ({ ...prev, bankIfscCode: 'Invalid IFSC format. Use 4 letters, then 0, then 6 characters (e.g. HDFC0001234).' }));
      setFormField('bankName', '');
      setFormField('bankBranchName', '');
      return;
    }
    setFieldErrors(prev => { const next = { ...prev }; delete next.bankIfscCode; return next; });
    setBankIfscLoading(true);
    try {
      const res = await verifyIfscCode(code);
      if (res.bankName) {
        setFormField('bankName', res.bankName);
        if (res.branchName) setFormField('bankBranchName', res.branchName);
        setFieldErrors(prev => {
          const next = { ...prev };
          delete next.bankName;
          delete next.bankBranchName;
          return next;
        });
      } else {
        setErrorMsg('IFSC lookup yielded empty results. Please enter bank details manually.');
      }
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.message || 'Could not verify IFSC right now. Please enter bank details manually.');
    } finally {
      setBankIfscLoading(false);
    }
  };

  const updateSpecialityFeeField = (rowIndex: number, channel: 'online' | 'offline', feeKey: 'firstVisitFees' | 'followUpFees', rawVal: string) => {
    let val = String(rawVal ?? '').replace(/\D/g, '');
    if (val && Number(val) > 10000) {
      val = '10000';
    }
    setForm(prev => {
      const list = [...(prev.specialityFeesList || [])];
      if (rowIndex < 0 || rowIndex >= list.length) return prev;
      const row = { ...list[rowIndex] };
      const btc = { ...(row.feesByConsultationType || {}) };
      const ch = { ...(btc[channel] || {}) };
      ch[feeKey] = val;
      btc[channel] = ch;
      row.feesByConsultationType = btc;
      list[rowIndex] = row;
      return { ...prev, specialityFeesList: list };
    });
  };

  // ─── Submit Onboarding ───────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step !== STEPS.length - 1) return;

    // Prevent click-through or double-click submit triggers
    if (Date.now() - lastStepChangeTimeRef.current < 500) return;

    // This is the ONLY place allowed to reveal every Step-5 validation error.
    setShowStepErrors(prev => ({
      ...prev,
      4: true,
    }));

    if (!validateAllSteps()) return;

    setIsSubmitting(true);
    setErrorMsg(null);
    try {
      const normalizeOtherSelections = (items: string[]) =>
        items.map(item => {
          const raw = String(item || '').trim();
          if (!raw || raw.toLowerCase() === 'other') return '';
          if (raw.toLowerCase().startsWith('other:')) return raw.split(':').slice(1).join(':').trim();
          return raw;
        }).filter(Boolean);

      const matrixFees = requiresSpecialityWiseFees(form.consultationTypes);
      const feeRowsForApi = matrixFees ? form.specialityFeesList : [];
      const derivedFees = pickDoctorLevelFeesFromMatrix(feeRowsForApi);

      const apiFormData = new FormData();

      const activeAdminId = user?.id || localStorage.getItem('userId') || sessionStorage.getItem('userId') || '';
      if (!activeAdminId) {
        setErrorMsg("Admin authentication session missing or expired. Please re-login to submit vendor onboarding.");
        setIsSubmitting(false);
        return;
      }
      const clinicData = {
        submitted_by: activeAdminId,
        doctorName: form.doctorName,
        clinicName: form.clinicName,
        category: form.category === 'Other' ? form.otherCategory : form.category,
        email: form.email,
        phoneNumber: form.phoneNumber,
        gender: form.gender,
        experienceYears: parseInt(form.experienceYears) || 0,
        educationalQualifications: normalizeOtherSelections(form.educationalQualifications),
        specializations: normalizeOtherSelections(form.specializations),
        languageProficiency: normalizeOtherSelections(form.languageProficiency),
        licenseNumber: form.licenseNumber,
        registrationCouncil: form.registrationCouncil === 'Other' ? form.otherRegistrationCouncil : form.registrationCouncil,
        medicalRegistrationCouncil: form.registrationCouncil === 'Other' ? form.otherRegistrationCouncil : form.registrationCouncil,
        otherRegistrationCouncil: form.registrationCouncil === 'Other' ? form.otherRegistrationCouncil : '',
        registrationDate: form.registrationDate,
        registrationExpiryDate: form.registrationExpiryDate,
        identityProofType: form.identityProofType,
        identityProofIssueDate: form.identityProofType === 'Driving License' ? form.identityProofIssueDate : '',
        identityProofExpiryDate: form.identityProofType === 'Driving License' ? form.identityProofExpiryDate : '',
        establishmentProof: normalizeEstablishmentRoleForApi(form.establishmentRole),
        establishmentProofType: form.establishmentProofType === 'Other' ? form.customEstablishmentProofName : form.establishmentProofType,

        firstTimeVisitFees: matrixFees ? derivedFees.first || '0' : '0',
        followUpFees: matrixFees ? derivedFees.follow || '0' : '0',
        specialityWiseFees: JSON.stringify(feeRowsForApi),
        consultationTypes: (form.consultationTypes || []).map((t: string) => t === 'Offline (In-Clinic)' ? 'Offline' : t),
        consultationDays: form.consultationDays,
        consultationTimeSlots: [],

        hasTelemedicineExperience: form.hasTelemedicineExperience,
        address: form.address || '',
        state: selectedState || form.state || '',
        city: form.city === 'Other' ? form.otherCity : form.city,
        pincode: form.pincode || '',
        nearbyLandmark: form.nearbyLandmark || '',
        floor: form.floor === 'Other' ? form.otherFloorNumber : form.floor,
        location: coordinates ? `${coordinates.lat},${coordinates.lng}` : '',
        latitude: coordinates?.lat || null,
        longitude: coordinates?.lng || null,
        doctorAbout: form.description || '',
        otherQualification: form.otherQualification || '',
        otherSpecialization: form.otherSpecialization || '',
        otherLanguage: form.otherLanguage || '',
        patientContactNumber: form.patientContactNumber || '',
        showPatientContactNumber: (form.consultationTypes || []).includes('Offline (In-Clinic)'),
        hasLiftAccess: !!form.hasLiftAccess,
        hasWheelchairAccess: !!form.hasWheelchairAccess,
        hasParking: !!form.hasParking,
        bankAccountHolderName: String(form.bankAccountHolderName || '').trim(),
        bankAccountNumber: String(form.bankAccountNumber || '').trim(),
        bankIfscCode: String(form.bankIfscCode || '').trim().toUpperCase(),
        bankName: String(form.bankName || '').trim(),
        bankBranchName: String(form.bankBranchName || '').trim(),
        bankAccountType: String(form.bankAccountType || 'Savings').trim(),
      };

      const vendorPayload = {
        vendorRole: 2,
        phoneNumber: form.phoneNumber,
        email: form.email.trim().toLowerCase(),
        password: 'Password@123',
        isTermsAccepted: !!form.isDeclarationAccepted,
        termsAcceptedAt: new Date().toISOString()
      };

      apiFormData.append('submitted_by', activeAdminId);
      apiFormData.append('phoneNumber', form.phoneNumber || '');
      apiFormData.append('email', form.email ? form.email.trim().toLowerCase() : '');
      apiFormData.append('clinicDetails', JSON.stringify(clinicData));
      apiFormData.append('clinic', JSON.stringify(clinicData));
      if (!isEditMode) {
        apiFormData.append('vendor', JSON.stringify(vendorPayload));
        apiFormData.append('bank', JSON.stringify({
          accountHolderName: clinicData.bankAccountHolderName,
          accountNumber: clinicData.bankAccountNumber,
          ifscCode: clinicData.bankIfscCode,
          bankName: clinicData.bankName,
          branchName: clinicData.bankBranchName,
          accountType: clinicData.bankAccountType.toUpperCase()
        }));
      }

      if (profilePic?.file) {
        apiFormData.append('profilePicture', profilePic.file);
      }

      const docsMetadata: any[] = [];
      const identityProofNames: string[] = [];
      let fileIndexCounter = 0;

      if (identityProofFront?.file) {
        apiFormData.append('documentFiles', identityProofFront.file);
        const name = `${form.identityProofType}_Front_Side`;
        docsMetadata.push({
          documentType: 'IDENTITY_PROOF',
          documentName: name,
          isLifetime: form.identityProofType !== 'Driving License',
          fileIndex: fileIndexCounter++,
          ...(form.identityProofType === 'Driving License' ? {
            issuedDate: form.identityProofIssueDate,
            expiryDate: form.identityProofExpiryDate
          } : {})
        });
        identityProofNames.push(name);
      }

      if (identityProofBack?.file) {
        apiFormData.append('documentFiles', identityProofBack.file);
        const name = `${form.identityProofType}_Back_Side`;
        docsMetadata.push({
          documentType: 'IDENTITY_PROOF',
          documentName: name,
          fileIndex: fileIndexCounter++,
          isLifetime: true
        });
        identityProofNames.push(name);
      }

      if (medicalReg?.file) {
        apiFormData.append('documentFiles', medicalReg.file);
        docsMetadata.push({
          documentType: 'REGISTRATION_CERTIFICATE',
          documentName: 'Medical Registration',
          documentNumber: form.licenseNumber,
          fileIndex: fileIndexCounter++,
          issuedDate: form.registrationDate,
          expiryDate: form.registrationExpiryDate,
          isLifetime: !form.registrationExpiryDate
        });
      }

      Object.keys(degreeCerts).forEach(q => {
        const fileObj = degreeCerts[q];
        if (fileObj?.file) {
          apiFormData.append('documentFiles', fileObj.file);
          docsMetadata.push({
            documentType: 'DEGREE_CERTIFICATE',
            documentName: `${q} Degree Certificate`,
            fileIndex: fileIndexCounter++,
            isLifetime: true
          });
        }
      });



      if (establishmentProof?.file) {
        apiFormData.append('documentFiles', establishmentProof.file);
        docsMetadata.push({
          documentType: 'ESTABLISHMENT_PROOF',
          documentName: clinicData.establishmentProofType || 'Establishment Proof',
          fileIndex: fileIndexCounter++,
          isLifetime: true
        });
      }

      apiFormData.append('documents', JSON.stringify(docsMetadata));
      apiFormData.append('identityProofDocumentNames', JSON.stringify(identityProofNames));

      const isOfflineSelected = form.consultationTypes.includes('Offline (In-Clinic)');
      if (isOfflineSelected) {
        clinicPhotos.forEach(photo => {
          if (photo.file) {
            apiFormData.append('clinicPhotos', photo.file);
          }
        });
      }

      if (isEditMode) {
        await updateClinicVendor(vendorId, apiFormData);
      } else {
        await onboardClinicVendor(apiFormData);
        localStorage.removeItem('vendorClinicRegistrationDraft_v1');
      }

      setSubmittedData({ doctorName: form.doctorName, email: form.email });
      setShowSuccess(true);
    } catch (err: any) {
      console.error('Doctor clinic registration submit failed:', err);
      const statusCode: number | undefined = err?.response?.status;
      const responseData = err?.response?.data || {};
      const backendCode = responseData?.code || responseData?.errorCode;
      const backendErrors = responseData?.errors;

      const errorsFromArray = Array.isArray(backendErrors)
        ? backendErrors.map((item: any) =>
          typeof item === 'string' ? item : item?.message || item?.msg || item?.field || ''
        ).filter(Boolean).join(', ')
        : '';
      const errorsFromObject =
        backendErrors && typeof backendErrors === 'object' && !Array.isArray(backendErrors)
          ? Object.entries(backendErrors as Record<string, any>)
            .map(([field, msg]) => `${field}: ${Array.isArray(msg) ? msg.join(', ') : String(msg || '')}`)
            .filter(Boolean).join(', ')
          : '';

      const apiMessage =
        responseData?.message ||
        responseData?.error ||
        responseData?.details?.message ||
        errorsFromArray ||
        errorsFromObject;

      let friendlyMessage = String(apiMessage || err?.message || '').trim();

      const isServerError =
        (typeof statusCode === 'number' && statusCode >= 500 && statusCode <= 599) ||
        /network error|timeout|connrefused|unreachable/i.test(err?.message || '');

      if (isServerError && !apiMessage) {
        friendlyMessage = `We are experiencing a server issue (HTTP ${statusCode || 'Connection Refused'}). Your registration could not be saved. Please try again in a few minutes, or contact support if it persists.`;
      } else {
        if (!friendlyMessage || /request failed with status code/i.test(friendlyMessage) || /network error/i.test(friendlyMessage)) {
          friendlyMessage = statusCode
            ? `Submission failed (HTTP ${statusCode}). Please verify required fields and try again.`
            : 'Submission failed. Please verify required fields and try again.';
        }
      }

      if (backendCode && !friendlyMessage.includes(String(backendCode))) {
        friendlyMessage = `${friendlyMessage} (Code: ${backendCode})`;
      }

      setErrorMsg(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Input styling helpers ──────────────────────────────────────────────────
  const inputClass = (name: string) =>
    `mt-1 block w-full rounded-lg border px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 text-black dark:text-white [&>option]:text-black [&>option]:bg-white [&>option]:dark:text-white [&>option]:dark:bg-gray-800 shadow-sm focus:ring-2 focus:ring-[#004275] focus:border-[#004275] transition-colors ${fieldErrors[name] ? 'border-red-400 ring-1 ring-red-500' : 'border-gray-300 dark:border-gray-600'}`;

  const labelClass = 'block text-sm font-semibold text-black dark:text-white mb-1';

  // ─── Render Steps ──────────────────────────────────────────────────────────
  const renderStep0 = () => (
    <div className="space-y-5">
      {/* Profile picture */}
      <div className="flex flex-col items-center">
        <label className={labelClass}>Profile Picture *</label>
        <div className="relative mt-2">
          <div className={`w-28 h-28 rounded-full border-4 ${fieldErrors.profilePicture ? 'border-red-500' : 'border-white dark:border-gray-700'} shadow-lg bg-white dark:bg-gray-800 overflow-hidden flex items-center justify-center`}>
            {profilePic?.preview || profilePreview ? (
              <img src={profilePic?.preview || profilePreview} alt="Profile" className="w-full h-full object-cover" style={{ transform: `scale(${imageScale})` }} />
            ) : (
              <svg className="w-14 h-14 text-gray-300 dark:text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
          </div>
          <label className="absolute bottom-0 right-0 bg-[#004275] hover:bg-[#003055] text-white p-2 rounded-full cursor-pointer shadow-md border-2 border-white dark:border-gray-800 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <input type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const url = reader.result as string;
                  setTempImageSrc(url);
                  setShowCropModal(true);
                  handleDocumentPick('profilePicture', file);
                };
                reader.readAsDataURL(file);
              }
            }} />
          </label>
        </div>
        <div className="h-5 mt-1">
          {fieldErrors.profilePicture && <p className="text-xs text-red-500">{fieldErrors.profilePicture}</p>}
        </div>
      </div>

      {/* Category */}
      <div ref={el => { fieldRefs.current.category = el; }}>
        <label className={labelClass}>Category *</label>
        <select value={form.category} onBlur={() => handleBlur('category')} onChange={e => setFormField('category', e.target.value)} className={inputClass('category')}>
          <option value="">Select Category</option>
          <option value="Doctor">Doctor</option>
          <option value="Veterinary Doctor">Veterinary Doctor</option>
          <option value="Nutritionist">Nutritionist</option>
          <option value="Physiotherapy">Physiotherapy</option>
          <option value="Other">Other</option>
        </select>
        {form.category === 'Other' && (
          <input type="text" value={form.otherCategory || ''} onBlur={() => handleBlur('otherCategory')} onChange={e => setFormField('otherCategory', e.target.value)} placeholder="Specify category" className={`${inputClass('otherCategory')} mt-2`} />
        )}
        <div className="h-5 mt-1">
          {(fieldErrors.category || fieldErrors.otherCategory) && <p className="text-xs text-red-500">{fieldErrors.category || fieldErrors.otherCategory}</p>}
        </div>
      </div>

      {/* Doctor Name */}
      <div ref={el => { fieldRefs.current.doctorName = el; }}>
        <label className={labelClass}>Doctor Name *</label>
        <input type="text" value={form.doctorName ?? ''} onBlur={() => handleBlur('doctorName')} onChange={e => setFormField('doctorName', e.target.value)} placeholder="Dr. Full Name" className={inputClass('doctorName')} />
        <div className="h-5 mt-1">
          {fieldErrors.doctorName && <p className="text-xs text-red-500">{fieldErrors.doctorName}</p>}
        </div>
      </div>

      {/* Clinic Name */}
      <div ref={el => { fieldRefs.current.clinicName = el; }}>
        <label className={labelClass}>Clinic / Business Name (Optional)</label>
        <input type="text" value={form.clinicName || ''} onBlur={() => handleBlur('clinicName')} onChange={e => setFormField('clinicName', e.target.value)} placeholder="Enter clinic name" className={inputClass('clinicName')} />
        <div className="h-5 mt-1">
          {fieldErrors.clinicName && <p className="text-xs text-red-500">{fieldErrors.clinicName}</p>}
        </div>
      </div>

      {/* Email */}
      <div ref={el => { fieldRefs.current.email = el; }}>
        <label className={labelClass}>Email Address *</label>
        <input type="email" value={form.email || ''} onBlur={() => handleBlur('email')} onChange={e => setFormField('email', e.target.value)} placeholder="doctor@example.com" className={inputClass('email')} />
        <div className="h-5 mt-1">
          {fieldErrors.email && <p className="text-xs text-red-500">{fieldErrors.email}</p>}
        </div>
      </div>

      {/* Gender */}
      <div ref={el => { fieldRefs.current.gender = el; }}>
        <label className={labelClass}>Gender *</label>
        <select value={form.gender || ''} onBlur={() => handleBlur('gender')} onChange={e => setFormField('gender', e.target.value)} className={inputClass('gender')}>
          <option value="">Select Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
        <div className="h-5 mt-1">
          {fieldErrors.gender && <p className="text-xs text-red-500">{fieldErrors.gender}</p>}
        </div>
      </div>

      {/* Phone Number */}
      <div ref={el => { fieldRefs.current.phoneNumber = el; }}>
        <label className={labelClass}>Phone Number *</label>
        <input type="tel" value={form.phoneNumber || ''} onBlur={() => handleBlur('phoneNumber')}
          onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
            setFormField('phoneNumber', val);
          }}
          maxLength={10} placeholder="Enter 10-digit number" className={inputClass('phoneNumber')} />
        <div className="h-5 mt-1">
          {fieldErrors.phoneNumber && <p className="text-xs text-red-500">{fieldErrors.phoneNumber}</p>}
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-5">
      {/* Experience */}
      <div ref={el => { fieldRefs.current.experienceYears = el; }}>
        <label className={labelClass}>Years of Experience *</label>
        <input type="number" min={0} max={99} value={form.experienceYears || ''} onBlur={() => handleBlur('experienceYears')} onChange={e => setFormField('experienceYears', e.target.value)} placeholder="e.g. 5" className={inputClass('experienceYears')} />
        <div className="h-5 mt-1">
          {fieldErrors.experienceYears && <p className="text-xs text-red-500">{fieldErrors.experienceYears}</p>}
        </div>
      </div>

      {/* Educational Qualifications */}
      <div ref={el => { fieldRefs.current.educationalQualifications = el; }}>
        <label className={labelClass}>Educational Qualifications *</label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Select all degrees you hold. Choose "Other" to add a custom one.</p>
        <CatalogMultiSelect
          label="qualifications"
          options={ALL_QUALIFICATIONS}
          values={form.educationalQualifications || []}
          onChange={vals => setFormField('educationalQualifications', vals)}
          error={fieldErrors.educationalQualifications}
        />
      </div>

      {/* Specializations */}
      <div ref={el => { fieldRefs.current.specializations = el; }}>
        <label className={labelClass}>Specializations *</label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Select all your specializations.</p>
        <CatalogMultiSelect
          label="specializations"
          options={ALL_SPECIALIZATIONS}
          values={form.specializations || []}
          onChange={vals => setFormField('specializations', vals)}
          error={fieldErrors.specializations}
        />
      </div>

      {/* Languages */}
      <div ref={el => { fieldRefs.current.languageProficiency = el; }}>
        <label className={labelClass}>Languages Spoken *</label>
        <CatalogMultiSelect
          label="languages"
          options={ALL_LANGUAGES}
          values={form.languageProficiency || []}
          onChange={vals => setFormField('languageProficiency', vals)}
          error={fieldErrors.languageProficiency}
        />
      </div>
    </div>
  );

  const renderStep2 = () => {
    const quals: string[] = (form.educationalQualifications || []).filter((q: string) => q && q !== 'Other');
    const specs: string[] = (form.specializations || []).filter((s: string) => s && s !== 'Other');
    return (
      <div className="space-y-4">
        {/* Medical Registration */}
        <DocumentationHeader title="Medical Registration" />
        <div ref={el => { fieldRefs.current.licenseNumber = el; }}>
          <label className={labelClass}>Registration Number *</label>
          <input type="text" value={form.licenseNumber || ''} onBlur={() => handleBlur('licenseNumber')} onChange={e => setFormField('licenseNumber', e.target.value)} placeholder="e.g. MH123456" className={inputClass('licenseNumber')} />
          <div className="h-5 mt-1">
            {fieldErrors.licenseNumber && <p className="text-xs text-red-500">{fieldErrors.licenseNumber}</p>}
          </div>
        </div>

        <div ref={el => { fieldRefs.current.registrationCouncil = el; }}>
          <label className={labelClass}>Medical Registration Council *</label>
          <select value={form.registrationCouncil || ''} onBlur={() => handleBlur('registrationCouncil')} onChange={e => setFormField('registrationCouncil', e.target.value)} className={inputClass('registrationCouncil')}>
            <option value="">Select Council</option>
            {ALL_COUNCILS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {form.registrationCouncil === 'Other' && (
            <input type="text" value={form.otherRegistrationCouncil || ''} onBlur={() => handleBlur('otherRegistrationCouncil')} onChange={e => setFormField('otherRegistrationCouncil', e.target.value)} placeholder="Specify council name" className={`${inputClass('otherRegistrationCouncil')} mt-2`} />
          )}
          <div className="h-5 mt-1">
            {fieldErrors.registrationCouncil && <p className="text-xs text-red-500">{fieldErrors.registrationCouncil}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div ref={el => { fieldRefs.current.registrationDate = el; }}>
            <label className={labelClass}>Registration Date *</label>
            <input type="date" value={form.registrationDate || ''} onBlur={() => handleBlur('registrationDate')} max={new Date().toLocaleDateString('en-CA')} onChange={e => setFormField('registrationDate', e.target.value)} className={inputClass('registrationDate')} />
            <div className="h-5 mt-1">
              {fieldErrors.registrationDate && <p className="text-xs text-red-500">{fieldErrors.registrationDate}</p>}
            </div>
          </div>
          <div ref={el => { fieldRefs.current.registrationExpiryDate = el; }}>
            <label className={labelClass}>Expiry Date *</label>
            <input
              type="date"
              value={form.registrationExpiryDate || ''}
              onBlur={() => handleBlur('registrationExpiryDate')}
              min={(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().split('T')[0]; })()}
              onChange={e => setFormField('registrationExpiryDate', e.target.value)}
              className={inputClass('registrationExpiryDate')}
            />
            <div className="h-5 mt-1">
              {fieldErrors.registrationExpiryDate && <p className="text-xs text-red-500">{fieldErrors.registrationExpiryDate}</p>}
            </div>
          </div>
        </div>

        <div ref={el => { fieldRefs.current.medicalRegistration = el; }}>
          <DocumentationRow label="Medical Registration Certificate" value={medicalReg?.file} onPick={f => handleDocumentPick('medicalRegistration', f)} error={fieldErrors.medicalRegistration} />
        </div>

        {/* Identity Proof */}
        <DocumentationHeader title="Identity Proof" />
        <div ref={el => { fieldRefs.current.identityProofType = el; }}>
          <label className={labelClass}>Identity Proof Type *</label>
          <select value={form.identityProofType || ''} onBlur={() => handleBlur('identityProofType')} onChange={e => setFormField('identityProofType', e.target.value)} className={inputClass('identityProofType')}>
            <option value="">Select Type</option>
            {IDENTITY_PROOF_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="h-5 mt-1">
            {fieldErrors.identityProofType && <p className="text-xs text-red-500">{fieldErrors.identityProofType}</p>}
          </div>
        </div>

        {form.identityProofType === 'Driving License' && (
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div ref={el => { fieldRefs.current.identityProofIssueDate = el; }}>
              <label className={labelClass}>Issue Date *</label>
              <input
                type="date"
                value={form.identityProofIssueDate || ''}
                onBlur={() => handleBlur('identityProofIssueDate')}
                max={new Date().toISOString().split('T')[0]}
                onChange={e => setFormField('identityProofIssueDate', e.target.value)}
                className={inputClass('identityProofIssueDate')}
              />
              <div className="h-5 mt-1">
                {fieldErrors.identityProofIssueDate && <p className="text-xs text-red-500">{fieldErrors.identityProofIssueDate}</p>}
              </div>
            </div>
            <div ref={el => { fieldRefs.current.identityProofExpiryDate = el; }}>
              <label className={labelClass}>Expiry Date *</label>
              <input
                type="date"
                value={form.identityProofExpiryDate || ''}
                onBlur={() => handleBlur('identityProofExpiryDate')}
                min={(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().split('T')[0]; })()}
                onChange={e => setFormField('identityProofExpiryDate', e.target.value)}
                className={inputClass('identityProofExpiryDate')}
              />
              <div className="h-5 mt-1">
                {fieldErrors.identityProofExpiryDate && <p className="text-xs text-red-500">{fieldErrors.identityProofExpiryDate}</p>}
              </div>
            </div>
          </div>
        )}

        {form.identityProofType && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div ref={el => { fieldRefs.current.identityProofFront = el; }}>
              <DocumentationRow
                label={`${form.identityProofType} – Front`}
                value={identityProofFront?.file}
                onPick={f => handleDocumentPick('identityProofFront', f)}
                error={fieldErrors.identityProofFront}
              />
            </div>
            {identityRequiresBack(form.identityProofType) && (
              <div ref={el => { fieldRefs.current.identityProofBack = el; }}>
                <DocumentationRow
                  label={`${form.identityProofType} – Back`}
                  value={identityProofBack?.file}
                  onPick={f => handleDocumentPick('identityProofBack', f)}
                  error={fieldErrors.identityProofBack}
                />
              </div>
            )}
          </div>
        )}

        {/* Degree Certificates */}
        {quals.length > 0 && (
          <div className="mt-4">
            <DocumentationHeader title="Degree Certificates" />
            <div className="grid grid-cols-1 gap-2">
              {quals.map((deg: string) => {
                const displayName = deg.startsWith('Other: ') ? deg.substring(7) : deg;
                return (
                  <div key={deg} ref={el => { fieldRefs.current[`degree_${deg}`] = el; }}>
                    <DocumentationRow
                      label={`${displayName} Degree Certificate`}
                      value={degreeCerts[deg]?.file}
                      onPick={f => handleDocumentPick('degreeCertificate', f, deg)}
                      error={fieldErrors[`degree_${deg}`]}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    );
  };

  const renderStep3 = () => {
    const isOfflineSelected = form.consultationTypes.includes('Offline (In-Clinic)');
    const isOnlineSelected = form.consultationTypes.includes('Online');
    const isInstantSelected = form.consultationTypes.includes('Instant');
    const matrixFees = requiresSpecialityWiseFees(form.consultationTypes);
    const specs = specsEligibleForFees(form.specializations);
    const establishmentProofTypes = getEstablishmentProofTypes(form.establishmentRole);

    return (
      <div className="space-y-5">
        <DocumentationHeader title="Consultation Details" />

        {/* Telemedicine toggle */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-gray-50 dark:bg-gray-800">
          <ToggleSwitch label="Has Telemedicine Experience" checked={!!form.hasTelemedicineExperience} onChange={v => setFormField('hasTelemedicineExperience', v)} />
          <p className="text-xs text-gray-500 mt-1">Do you have experience with online consultations?</p>
        </div>

        {/* Consultation Types */}
        <div ref={el => { fieldRefs.current.consultationTypes = el; }}>
          <label className={labelClass}>Consultation Types *</label>
          <CatalogMultiSelect
            label="consultation types"
            options={CONSULTATION_TYPES}
            values={form.consultationTypes || []}
            onChange={vals => setFormField('consultationTypes', vals)}
            error={fieldErrors.consultationTypes}
          />
          <div className="h-2" />
        </div>

        {/* Consultation Days */}
        <div ref={el => { fieldRefs.current.consultationDays = el; }}>
          <label className={labelClass}>Consultation Days *</label>
          <CatalogMultiSelect
            label="consultation days"
            options={DAYS_LIST}
            values={form.consultationDays || []}
            onChange={vals => setFormField('consultationDays', vals)}
            error={fieldErrors.consultationDays}
          />
          <div className="h-2" />
        </div>

        {/* Instant Consultation Banner */}
        {isInstantSelected && (
          <div className="bg-[#004275]/10 dark:bg-[#38bdf8]/10 border border-[#004275]/30 dark:border-[#38bdf8]/30 rounded-xl p-4 flex gap-3 text-sm text-[#004275] dark:text-[#38bdf8]">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-bold">Instant Consultation Fees</div>
              <div className="mt-1 text-xs">Instant consultation fees are set by Vedika.Health according to your speciality.</div>
            </div>
          </div>
        )}

        {/* Speciality-wise Fees Matrix */}
        {matrixFees && (
          <div ref={el => { fieldRefs.current.specialityFeesList = el; }} className="border border-gray-100 dark:border-gray-700 rounded-xl p-4 bg-gray-50 dark:bg-gray-800 space-y-4">
            <div>
              <h4 className="text-sm font-bold text-[#004275] dark:text-[#38bdf8]">Speciality-wise Fees *</h4>
              <p className="text-xs text-gray-500 mt-1">Enter first visit and follow-up fee (max ₹10,000) for each specialization.</p>
            </div>
            {specs.length === 0 ? (
              <p className="text-xs text-red-500 font-semibold">Please select specialization in the previous step before entering fees.</p>
            ) : (
              <div className="space-y-4">
                {form.specialityFeesList.map((feeRow: any, index: number) => (
                  <div key={feeRow.speciality} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3 space-y-3">
                    <div className="text-sm font-bold text-black dark:text-white">{feeRow.speciality}</div>

                    {isOnlineSelected && (
                      <div className="space-y-2">
                        <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">Online Consultation Fees</div>
                        <div className="grid grid-cols-2 gap-2">
                          <input type="text" placeholder="First Visit Fees *" value={feeRow.feesByConsultationType?.online?.firstVisitFees || ''} onChange={e => updateSpecialityFeeField(index, 'online', 'firstVisitFees', e.target.value)} className="w-full text-xs border border-gray-300 dark:border-gray-750 rounded p-2 focus:ring-1 focus:ring-[#004275] bg-gray-50 dark:bg-gray-800 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500" />
                          <input type="text" placeholder="Follow Up Fees *" value={feeRow.feesByConsultationType?.online?.followUpFees || ''} onChange={e => updateSpecialityFeeField(index, 'online', 'followUpFees', e.target.value)} className="w-full text-xs border border-gray-300 dark:border-gray-750 rounded p-2 focus:ring-1 focus:ring-[#004275] bg-gray-50 dark:bg-gray-800 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500" />
                        </div>
                      </div>
                    )}

                    {isOfflineSelected && (
                      <div className="space-y-2">
                        <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">In-clinic (Offline) Fees</div>
                        <div className="grid grid-cols-2 gap-2">
                          <input type="text" placeholder="First Visit Fees *" value={feeRow.feesByConsultationType?.offline?.firstVisitFees || ''} onChange={e => updateSpecialityFeeField(index, 'offline', 'firstVisitFees', e.target.value)} className="w-full text-xs border border-gray-300 dark:border-gray-750 rounded p-2 focus:ring-1 focus:ring-[#004275] bg-gray-50 dark:bg-gray-800 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500" />
                          <input type="text" placeholder="Follow Up Fees *" value={feeRow.feesByConsultationType?.offline?.followUpFees || ''} onChange={e => updateSpecialityFeeField(index, 'offline', 'followUpFees', e.target.value)} className="w-full text-xs border border-gray-300 dark:border-gray-750 rounded p-2 focus:ring-1 focus:ring-[#004275] bg-gray-50 dark:bg-gray-800 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="h-5">
              {fieldErrors.specialityFeesList && <p className="text-xs text-red-500">{fieldErrors.specialityFeesList}</p>}
            </div>
          </div>
        )}

        {/* Business details */}
        <Divider />
        <DocumentationHeader title="Business Details" />

        <div ref={el => { fieldRefs.current.establishmentRole = el; }}>
          <label className={labelClass}>Business Category *</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ESTABLISHMENT_ROLES.map(role => {
              const sel = form.establishmentRole === role.value;
              return (
                <button key={role.value} type="button" onClick={() => {
                  setFormField('establishmentRole', role.value);
                  setFormField('establishmentProofType', '');
                  setFormField('customEstablishmentProofName', '');
                }}
                  className={`text-left px-3 py-2.5 rounded-xl border transition-all ${sel ? 'bg-[#004275]/10 border-[#004275] text-[#004275]' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-[#004275]/50'}`}>
                  <div className="font-bold text-xs">{role.label}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{role.proof}</div>
                </button>
              );
            })}
          </div>
          <div className="h-5 mt-1" />
        </div>

        {form.establishmentRole && (
          <div ref={el => { fieldRefs.current.establishmentProofType = el; }}>
            <label className={labelClass}>Accepted proofs (Any one) *</label>
            <select value={form.establishmentProofType} onBlur={() => handleBlur('establishmentProofType')} onChange={e => setFormField('establishmentProofType', e.target.value)} className={inputClass('establishmentProofType')}>
              <option value="">Select Accepted Proof Type</option>
              {establishmentProofTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {form.establishmentProofType === 'Other' && (
              <input type="text" value={form.customEstablishmentProofName || ''} onBlur={() => handleBlur('customEstablishmentProofName')} onChange={e => setFormField('customEstablishmentProofName', e.target.value)} placeholder="Specify document name" className={`${inputClass('customEstablishmentProofName')} mt-2`} />
            )}
            <div className="h-5 mt-1">
              {(fieldErrors.establishmentProofType || fieldErrors.customEstablishmentProofName) && <p className="text-xs text-red-500">{fieldErrors.establishmentProofType || fieldErrors.customEstablishmentProofName}</p>}
            </div>
          </div>
        )}

        {form.establishmentRole && (
          <div ref={el => { fieldRefs.current.establishmentProof = el; }}>
            <DocumentationRow
              label="Establishment Proof File"
              value={establishmentProof?.file}
              onPick={f => handleDocumentPick('establishmentProof', f)}
              error={fieldErrors.establishmentProof}
              disabled={!form.establishmentProofType || (form.establishmentProofType === 'Other' && !form.customEstablishmentProofName?.trim())}
            />
          </div>
        )}
      </div>
    );
  };

  const renderStep4 = () => {
    const isOfflineSelected = form.consultationTypes.includes('Offline (In-Clinic)');
    return (
      <div className="space-y-5">
        {/* Conditional Address details */}
        {isOfflineSelected && (
          <>
            <DocumentationHeader title="Clinic/Business Address" />

            <div ref={el => { fieldRefs.current.address = el; }}>
              <label className={labelClass}>Full Address *</label>
              <textarea value={form.address || ''} onBlur={() => handleBlur('address')} onChange={e => setFormField('address', e.target.value)} rows={3} placeholder="Enter full clinic address" className={inputClass('address')} />
              <div className="h-5 mt-1">
                {fieldErrors.address && <p className="text-xs text-red-500">{fieldErrors.address}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div ref={el => { fieldRefs.current.state = el; }}>
                <label className={labelClass}>State *</label>
                <select value={selectedState || form.state || ''} onBlur={() => handleBlur('state')} onChange={e => {
                  setSelectedState(e.target.value);
                  setFormField('state', e.target.value);
                  setFormField('city', '');
                  const found = stateCityList.find((s: any) => s.state === e.target.value);
                  setAvailableCities(found ? found.cities : []);
                }} className={inputClass('state')}>
                  <option value="">Select State</option>
                  {stateCityList.map((s: any) => <option key={s.state} value={s.state}>{s.state}</option>)}
                </select>
                <div className="h-5 mt-1">
                  {fieldErrors.state && <p className="text-xs text-red-500">{fieldErrors.state}</p>}
                </div>
              </div>

              <div ref={el => { fieldRefs.current.city = el; }}>
                <label className={labelClass}>City *</label>
                <select value={form.city || ''} onBlur={() => handleBlur('city')} onChange={e => setFormField('city', e.target.value)} disabled={!selectedState} className={inputClass('city')}>
                  <option value="">{selectedState ? 'Select City' : 'Select State First'}</option>
                  {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                  {selectedState && <option value="Other">Other</option>}
                </select>
                {form.city === 'Other' && (
                  <input type="text" value={form.otherCity || ''} onBlur={() => handleBlur('otherCity')} onChange={e => setFormField('otherCity', e.target.value)} placeholder="Specify City Name" className={`${inputClass('otherCity')} mt-2`} />
                )}
                <div className="h-5 mt-1">
                  {(fieldErrors.city || fieldErrors.otherCity) && <p className="text-xs text-red-500">{fieldErrors.city || fieldErrors.otherCity}</p>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div ref={el => { fieldRefs.current.pincode = el; }}>
                <label className={labelClass}>Pincode *</label>
                <input type="text" value={form.pincode || ''} onBlur={() => handleBlur('pincode')} onChange={e => setFormField('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} placeholder="6-digit pincode" className={inputClass('pincode')} />
                <div className="h-5 mt-1">
                  {fieldErrors.pincode && <p className="text-xs text-red-500">{fieldErrors.pincode}</p>}
                </div>
              </div>
              <div>
                <label className={labelClass}>Nearby Landmark</label>
                <input type="text" value={form.nearbyLandmark || ''} onChange={e => setFormField('nearbyLandmark', e.target.value)} placeholder="e.g. Near Apollo Hospital" className={inputClass('nearbyLandmark')} />
                <div className="h-5 mt-1" />
              </div>
            </div>

            <div ref={el => { fieldRefs.current.floor = el; }}>
              <label className={labelClass}>Floor *</label>
              <select value={form.floor || ''} onBlur={() => handleBlur('floor')} onChange={e => setFormField('floor', e.target.value)} className={inputClass('floor')}>
                <option value="">Select Floor</option>
                {FLOOR_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              {form.floor === 'Other' && (
                <input type="text" value={form.otherFloorNumber || ''} onBlur={() => handleBlur('otherFloorNumber')} onChange={e => setFormField('otherFloorNumber', e.target.value)} placeholder="Enter floor number" className={`${inputClass('otherFloorNumber')} mt-2`} />
              )}
              <div className="h-5 mt-1">
                {(fieldErrors.floor || fieldErrors.otherFloorNumber) && <p className="text-xs text-red-500">{fieldErrors.floor || fieldErrors.otherFloorNumber}</p>}
              </div>
            </div>

            {/* Facilities */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl px-4 divide-y divide-gray-100 dark:divide-gray-700 bg-gray-50 dark:bg-gray-800">
              <ToggleSwitch label="Lift / Elevator Access" checked={!!form.hasLiftAccess} onChange={v => setFormField('hasLiftAccess', v)} />
              <ToggleSwitch label="Wheelchair Access" checked={!!form.hasWheelchairAccess} onChange={v => setFormField('hasWheelchairAccess', v)} />
              <ToggleSwitch label="Parking Available" checked={!!form.hasParking} onChange={v => setFormField('hasParking', v)} />
            </div>
            <div className="h-2" />

            {/* Location */}
            <div ref={el => { fieldRefs.current.locationLink = el; }}>
              <label className={labelClass}>Clinic Map Location *</label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowMapDialog(true)}
                  className="px-4 py-2.5 bg-[#004275] text-white rounded-lg hover:bg-[#003055] transition-colors text-sm font-semibold"
                >
                  {coordinates ? 'Adjust Location on Map' : 'Select Location on Map'}
                </button>
                <input
                  type="text"
                  value={form.locationLink || ''}
                  onBlur={() => handleBlur('locationLink')}
                  onChange={e => {
                    const val = e.target.value;
                    setFormField('locationLink', val);
                    const parsed = val.split(',').map(n => parseFloat(n.trim()));
                    if (parsed.length === 2 && !isNaN(parsed[0]) && !isNaN(parsed[1])) {
                      setCoordinates({ lat: parsed[0], lng: parsed[1] });
                    }
                  }}
                  placeholder="e.g. Swargate, Pune or location details"
                  className={inputClass('locationLink')}
                />
              </div>
              <div className="h-5 mt-1">
                {fieldErrors.locationLink && <p className="text-xs text-red-500">{fieldErrors.locationLink}</p>}
              </div>
            </div>

            {/* Clinic Photos */}
            <div ref={el => { fieldRefs.current.clinicPhotos = el; }}>
              <label className={labelClass}>Clinic Photos *</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Upload at least 3 and up to 5 photos of your clinic.</p>
              <input type="file" multiple accept="image/*" disabled={clinicPhotos.length >= 5} onChange={e => handleClinicPhotoAdd(e.target.files)} className="block w-full text-xs text-gray-400 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#004275] file:text-white hover:file:bg-[#003055] cursor-pointer disabled:opacity-50" />
              {clinicPhotos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                  {clinicPhotos.map((photo, i) => (
                    <div key={i} className="relative group rounded-lg overflow-hidden border border-gray-200 h-16 w-full">
                      <img src={photo.preview} alt={`Clinic ${i + 1}`} className="object-cover w-full h-full" />
                      <button type="button" onClick={() => removeClinicPhoto(i)} className="absolute top-0 right-0 p-1 bg-red-600 text-white rounded-bl hover:bg-red-700 transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="h-5 mt-1">
                {fieldErrors.clinicPhotos && <p className="text-xs text-red-500">{fieldErrors.clinicPhotos}</p>}
              </div>
            </div>

            {/* Clinic Contact */}
            <div ref={el => { fieldRefs.current.patientContactNumber = el; }}>
              <label className={labelClass}>Clinic Contact Number *</label>
              <input type="tel" value={form.patientContactNumber || ''} onBlur={() => handleBlur('patientContactNumber')} onChange={e => setFormField('patientContactNumber', e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10} placeholder="10-digit mobile number" className={inputClass('patientContactNumber')} />
              <div className="h-5 mt-1">
                {fieldErrors.patientContactNumber && <p className="text-xs text-red-500">{fieldErrors.patientContactNumber}</p>}
              </div>
            </div>
          </>
        )}

        {/* About */}
        <div ref={el => { fieldRefs.current.description = el; }}>
          <label className={labelClass}>About Yourself *</label>
          <textarea value={form.description || ''} onBlur={() => handleBlur('description')} onChange={e => setFormField('description', e.target.value)} rows={3} maxLength={5000} placeholder="Describe your background, specialties, and experience..." className={inputClass('description')} />
          <div className="flex justify-between items-center text-[10px] text-gray-500 mt-1">
            <span>Max 5000 characters</span>
            <span>{(form.description || '').length}/5000</span>
          </div>
          <div className="h-5">
            {fieldErrors.description && <p className="text-xs text-red-500">{fieldErrors.description}</p>}
          </div>
        </div>

        {/* Bank Details */}
        {!isEditMode && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-5 space-y-4">
            <h3 className="text-base font-bold text-[#004275] dark:text-[#38bdf8]">Settlement Bank Details</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div ref={el => { fieldRefs.current.bankAccountHolderName = el; }}>
                <label className={labelClass}>Account Holder Name *</label>
                <input type="text" value={form.bankAccountHolderName || ''} onBlur={() => handleBlur('bankAccountHolderName')} onChange={e => setFormField('bankAccountHolderName', e.target.value)} placeholder="As per bank records" className={inputClass('bankAccountHolderName')} />
                <div className="h-5 mt-1">
                  {fieldErrors.bankAccountHolderName && <p className="text-xs text-red-500">{fieldErrors.bankAccountHolderName}</p>}
                </div>
              </div>

              <div ref={el => { fieldRefs.current.bankAccountNumber = el; }}>
                <label className={labelClass}>Account Number *</label>
                <input type="text" value={form.bankAccountNumber || ''} onBlur={() => handleBlur('bankAccountNumber')} onChange={e => setFormField('bankAccountNumber', e.target.value)} placeholder="Bank account number" className={inputClass('bankAccountNumber')} />
                <div className="h-5 mt-1">
                  {fieldErrors.bankAccountNumber && <p className="text-xs text-red-500">{fieldErrors.bankAccountNumber}</p>}
                </div>
              </div>
            </div>

            <div ref={el => { fieldRefs.current.bankIfscCode = el; }}>
              <label className={labelClass}>IFSC Code *</label>
              <div className="relative">
                <input type="text" value={form.bankIfscCode || ''} onBlur={() => handleBlur('bankIfscCode')} onChange={e => {
                  const val = e.target.value.toUpperCase();
                  setFormField('bankIfscCode', val);
                  if (val.length === 11) verifyIfsc(val);
                }} placeholder="e.g. HDFC0001234" maxLength={11} className={inputClass('bankIfscCode')} />
                {bankIfscLoading && <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-[#004275]" />}
              </div>
              <div className="h-5 mt-1">
                {fieldErrors.bankIfscCode && <p className="text-xs text-red-500">{fieldErrors.bankIfscCode}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div ref={el => { fieldRefs.current.bankName = el; }}>
                <label className={labelClass}>Bank Name *</label>
                <input type="text" value={form.bankName || ''} onBlur={() => handleBlur('bankName')} onChange={e => setFormField('bankName', e.target.value)} placeholder="Bank name" className={inputClass('bankName')} />
                <div className="h-5 mt-1">
                  {fieldErrors.bankName && <p className="text-xs text-red-500">{fieldErrors.bankName}</p>}
                </div>
              </div>
              <div>
                <label className={labelClass}>Branch Name</label>
                <input type="text" value={form.bankBranchName || ''} onChange={e => setFormField('bankBranchName', e.target.value)} placeholder="Branch name" className={inputClass('bankBranchName')} />
                <div className="h-5 mt-1" />
              </div>
            </div>

            <div ref={el => { fieldRefs.current.bankAccountType = el; }}>
              <label className={labelClass}>Account Type *</label>
              <select value={form.bankAccountType || 'Savings'} onBlur={() => handleBlur('bankAccountType')} onChange={e => setFormField('bankAccountType', e.target.value)} className={inputClass('bankAccountType')}>
                <option value="Savings">Savings</option>
                <option value="Current">Current</option>
              </select>
              <div className="h-5 mt-1">
                {fieldErrors.bankAccountType && <p className="text-xs text-red-500">{fieldErrors.bankAccountType}</p>}
              </div>
            </div>
          </div>
        )}

      </div >
    );
  };

  // ─── Crop Modal ─────────────────────────────────────────────────────────────
  const CropModal = () => (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4 text-center">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Adjust Profile Image</h3>
        <div className="w-44 h-44 mx-auto rounded-full border-4 border-[#004275] overflow-hidden flex items-center justify-center bg-black/10">
          <img src={tempImageSrc!} alt="Preview" className="w-full h-full object-cover" style={{ transform: `scale(${imageScale})` }} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">Zoom ({Math.round(imageScale * 100)}%)</label>
          <input type="range" min="1" max="2.5" step="0.05" value={imageScale} onChange={e => setImageScale(parseFloat(e.target.value))} className="w-full accent-[#004275]" />
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={() => setShowCropModal(false)} className="flex-1 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 font-semibold text-gray-800 dark:text-gray-200">Cancel</button>
          <button type="button" onClick={() => setShowCropModal(false)} className="flex-1 py-2 rounded-lg bg-[#004275] text-white font-semibold hover:bg-[#003055]">Set Photo</button>
        </div>
      </div>
    </div>
  );

  // ─── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-6 mb-10 text-black dark:text-white">

      {/* Stepper */}
      <div className="mb-8 pt-2">
        <Stepper
          steps={STEPS.map(title => ({ title }))}
          activeStep={showSuccess ? STEPS.length : step}
          activeColor="#ffffff"
          completeColor="#22c55e"
          defaultColor="#e5e7eb"
          activeTitleColor="#ffffff"
          completeTitleColor="#22c55e"
          defaultTitleColor="#6b7280"
          defaultCircleFontColor="#6b7280"
          activeCircleFontColor="#004275"
          completeCircleFontColor="#ffffff"
          size={40}
          circleFontSize={18}
          titleFontSize={12}
          completeBarColor="#22c55e"
          defaultBarColor="#e5e7eb"
        />
      </div>

      {/* Crop Modal */}
      {showCropModal && tempImageSrc && <CropModal />}

      {/* Map Dialog Box */}
      {showMapDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-xl w-full shadow-2xl space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Select Location on Map</h3>
              <button type="button" onClick={() => setShowMapDialog(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search clinic location (e.g. Swargate, Pune)..."
                  value={mapSearchText}
                  onChange={e => setMapSearchText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleMapSearch(mapSearchText);
                    }
                  }}
                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-black dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#004275]"
                />
                <button
                  type="button"
                  onClick={() => handleMapSearch(mapSearchText)}
                  disabled={mapSearchLoading}
                  className="px-4 py-2 bg-[#004275] text-white rounded-lg hover:bg-[#003055] transition-colors text-sm font-semibold disabled:opacity-50"
                >
                  {mapSearchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>

              <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <MapComponent
                  coordinates={coordinates}
                  onGetLocation={getCurrentLocation}
                  isGettingLocation={isGettingLocation}
                  onChangeCoordinates={(coords) => {
                    setCoordinates(coords);
                    reverseGeocodeLat(coords.lat, coords.lng);
                  }}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowMapDialog(false)}
                className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 font-semibold text-gray-800 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (mapSearchText?.trim()) {
                    setFormField('locationLink', mapSearchText.trim());
                  } else if (coordinates) {
                    const formatted = `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`;
                    setFormField('locationLink', formatted);
                  }
                  setShowMapDialog(false);
                }}
                className="px-4 py-2 rounded-lg bg-[#004275] text-white font-semibold hover:bg-[#003055]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success */}
      {showSuccess ? (
        <div className="flex flex-col items-center justify-center py-16 text-center max-w-lg mx-auto">
          <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-green-600 mb-3">{isEditMode ? 'Doctor Clinic Updated Successfully!' : 'Doctor Clinic Onboarded Successfully!'}</h2>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 leading-relaxed mb-6">
            Your registration request has been submitted successfully. Our team will review your profile and revert within 5 to 7 business days.
          </p>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6 w-full max-w-md text-xs text-left">
            <div><span className="font-semibold">Doctor:</span> {submittedData?.doctorName}</div>
            <div><span className="font-semibold">Email:</span> {submittedData?.email}</div>
          </div>
          <button onClick={() => {
            if (isEditMode) navigate(routeLocation.pathname.startsWith('/vendors') ? '/vendors' : '/sales/vendor-onboarding');
            else {
              setForm({
                doctorName: 'Dr. ', category: '', otherCategory: '', clinicName: '', email: '', gender: '', phoneNumber: '',
                experienceYears: '', educationalQualifications: [], otherQualification: '',
                specializations: [], otherSpecialization: '',
                languageProficiency: [], otherLanguage: '',
                licenseNumber: '', registrationCouncil: '', otherRegistrationCouncil: '', registrationDate: '', registrationExpiryDate: '',
                identityProofType: '', identityProofIssueDate: '', identityProofExpiryDate: '',
                degreeCertificates: {}, specializationCertificates: {},
                consultationTypes: [], consultationDays: [], specialityFeesList: [],
                hasTelemedicineExperience: false,
                establishmentRole: 'Owner of establishment', establishmentProofType: '', customEstablishmentProofName: '',
                address: '', state: '', city: '', otherCity: '', pincode: '', nearbyLandmark: '', floor: '', otherFloorNumber: '',
                hasLiftAccess: false, hasWheelchairAccess: false, hasParking: false,
                description: '', patientContactNumber: '', locationLink: '',
                bankAccountHolderName: '', bankAccountNumber: '', bankIfscCode: '', bankName: '', bankBranchName: '', bankAccountType: 'Savings',
                isDeclarationAccepted: false,
              });
              setProfilePic(null);
              setIdentityProofFront(null);
              setIdentityProofBack(null);
              setMedicalReg(null);
              setEstablishmentProof(null);
              setDegreeCerts({});
              setSpecializationCerts({});
              setClinicPhotos([]);
              setStep(0);
              setShowSuccess(false);
              localStorage.removeItem('vendorClinicRegistrationDraft_v1');
            }
          }} className="px-6 py-2 rounded-lg font-semibold bg-[#004275] text-white hover:bg-[#003055] transition-colors">
            {isEditMode ? 'Back to Vendor List' : 'Add Another Doctor Clinic'}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} onKeyDown={e => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA' && (e.target as HTMLElement).tagName !== 'BUTTON') e.preventDefault(); }} className="space-y-4">
          {/* Error banner */}
          {errorMsg && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1 text-sm text-red-700 dark:text-red-300">{errorMsg}</div>
              <button type="button" onClick={() => setErrorMsg(null)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* Step content */}
          {step === 0 && renderStep0()}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}

          {/* Nav buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
            <button type="button" onClick={prevStep} disabled={step === 0}
              className="px-6 py-2.5 rounded-lg font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50 hover:bg-gray-300 transition-colors">
              Back
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={nextStep}
                className="px-6 py-2.5 rounded-lg font-semibold bg-[#004275] text-white hover:bg-[#003055] transition-colors">
                Next
              </button>
            ) : (
              <button type="submit" disabled={isSubmitting}
                className="px-6 py-2.5 rounded-lg font-semibold bg-gradient-to-r from-[#004275] to-[#0066b2] text-white shadow-md hover:from-[#003055] hover:to-[#004275] transition-all flex items-center gap-2 min-w-[180px] justify-center">
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSubmitting ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update Doctor Clinic' : 'Submit Registration')}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default DoctorClinicVendorForm;